import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ScrollText, Search, Eye, X, Plus, Trash2, Send, RefreshCw, AlertCircle, ChevronLeft, ChevronRight, Lock, FileText, Calendar, Users, Building2, ClipboardList, Info, Upload, PencilLine, Undo2, Key, CheckCircle } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

const PAGE_SIZE = 20;

const STAT_TONES = {
  slate: 'bg-slate-50 border-slate-200 text-slate-900',
  green: 'bg-green-50 border-green-200 text-green-900',
  amber: 'bg-amber-50 border-amber-200 text-amber-900',
  blue: 'bg-blue-50 border-blue-200 text-blue-900',
  gray: 'bg-gray-50 border-gray-200 text-gray-800',
  purple: 'bg-purple-50 border-purple-200 text-purple-900',
};

/** Kafelek licznika umów w nagłówku modułu. */
const StatTile = ({ label, value, tone = 'slate' }) => (
  <div className={`rounded-lg border px-2.5 py-2 ${STAT_TONES[tone] || STAT_TONES.slate}`}>
    <div className="text-lg font-semibold leading-tight">{value ?? 0}</div>
    <div className="text-[11px] leading-tight opacity-80">{label}</div>
  </div>
);

const RODZAJ_STRONY = [
  { code: 'SU01', label: 'Przedsiębiorca' },
  { code: 'SU02', label: 'Osoba fizyczna' },
  { code: 'SU03', label: 'JSFP' },
];

const RODZAJ_ZMIANY = [
  { code: 'TSU01', label: 'Korekta danych' },
  { code: 'TSU02', label: 'Aneks do umowy' },
  { code: 'TSU03', label: 'Zmiana w zakresie wyłączenia jawności danych' },
  { code: 'TSU04', label: 'Zmiana danych strony umowy' },
  { code: 'TSU05', label: 'Rozwiązanie umowy' },
  { code: 'TSU06', label: 'Wypowiedzenie umowy' },
  { code: 'TSU07', label: 'Odstąpienie od umowy' },
  { code: 'TSU10', label: 'Wygaśnięcie umowy' },
  { code: 'TSU11', label: 'Cesja umowy' },
  { code: 'inne', label: 'Inne' },
];

const OKRES_JEDNOSTKA = ['dzień', 'miesiąc', 'rok'];

// Podstawy prawne wyłączenia jawności (CRU słownik `podstawa`)
const PODSTAWA_NIEJAWNOSCI = [
  { code: 'ART_5_UST_1', label: 'Art. 5 ust. 1 – ochrona informacji niejawnych / tajemnic ustawowych' },
  { code: 'ART_5_UST_2', label: 'Art. 5 ust. 2 – prywatność osoby fizycznej / tajemnica przedsiębiorcy' },
  { code: 'ART_5_UST_2A', label: 'Art. 5 ust. 2a – ustawy o dostępie do informacji publicznej' },
  { code: 'ART_5_UST_2B', label: 'Art. 5 ust. 2b – ustawy o dostępie do informacji publicznej' },
  { code: 'INNA', label: 'Inna podstawa prawna (wymaga komentarza)' },
];

const podstawaNiejawnosciLabel = (code) =>
  PODSTAWA_NIEJAWNOSCI.find((p) => p.code === code)?.label || code || '—';

const emptyNiejawnosc = () => ({ on: false, podstawa: 'ART_5_UST_2', komentarz: '' });

/** Buduje obiekt niejawności zgodnie z CRU (komentarz tylko gdy podstawa=INNA). */
const buildNiejawnoscPayload = (n) => {
  if (!n || !n.on) return undefined;
  const obj = { podstawa: n.podstawa };
  if (n.podstawa === 'INNA' && n.komentarz.trim()) {
    obj.komentarz = n.komentarz.trim();
  }
  return obj;
};

/** Waliduje niejawność: gdy podstawa=INNA, komentarz jest wymagany. */
const validateNiejawnosc = (n, label) => {
  if (!n || !n.on) return null;
  if (!n.podstawa) return `${label}: wybierz podstawę prawną wyłączenia jawności.`;
  if (n.podstawa === 'INNA' && !n.komentarz.trim()) {
    return `${label}: dla podstawy „Inna” wymagany jest komentarz uzasadniający.`;
  }
  return null;
};

const CRU_MIN_DATE = '2025-11-17';
const CRU_OKRES_MIN_DATE = '2026-01-01';
const CRU_OKRES_MAX_DATE = '2500-12-31';

const getTodayIso = () => new Date().toISOString().slice(0, 10);

const getOkresOdMaxIso = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

const isoToLocalDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Konwersja z natywnego inputu date (yyyy-mm-dd) na format CRU (dd.MM.yyyy)
const toCruDate = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return '';
  return `${d}.${m}.${y}`;
};

/** CRU wymaga formatu 1234.56 – kropka dziesiętna, dokładnie 2 cyfry po kropce. */
const formatCruMoney = (raw) => {
  if (!raw || !raw.trim()) return { value: null, error: null };

  const normalized = raw.trim().replace(/\s/g, '').replace(',', '.');

  if (/^\d+\.\d{2}$/.test(normalized)) {
    const amount = Number(normalized);
    if (!Number.isFinite(amount) || amount < 0 || amount > 1_000_000_000_000) {
      return { value: null, error: 'Wartość przedmiotu musi być liczbą od 0.00 do 1000000000000.00.' };
    }
    return { value: normalized, error: null };
  }

  if (/^\d+$/.test(normalized)) {
    return { value: `${normalized}.00`, error: null };
  }

  if (/^\d+\.\d+$/.test(normalized)) {
    const [, decimals] = normalized.split('.');
    if (decimals.length === 3) {
      return {
        value: null,
        error: 'Wpisano np. „22.000” – to wygląda na separator tysięcy. CRU oczekuje kwoty z kropką dziesiętną, np. 22000.00.',
      };
    }
    const amount = Number(normalized);
    if (!Number.isFinite(amount) || amount < 0) {
      return { value: null, error: 'Wartość przedmiotu musi być poprawną liczbą, np. 22000.00.' };
    }
    return { value: amount.toFixed(2), error: null };
  }

  return {
    value: null,
    error: 'Wartość przedmiotu musi mieć format 1234.56 (kropka, dokładnie 2 miejsca po kropce).',
  };
};

const validateCruDate = (isoDate, label) => {
  if (!isoDate) return `${label}: podaj datę.`;
  const date = isoToLocalDate(isoDate);
  const min = isoToLocalDate(CRU_MIN_DATE);
  const max = isoToLocalDate(getTodayIso());
  if (date < min) return `${label}: nie może być wcześniejsza niż 17.11.2025.`;
  if (date > max) {
    return `${label}: nie może być w przyszłości (maksymalnie dzisiaj: ${toCruDate(getTodayIso())}).`;
  }
  return null;
};

/** Walidacja dat okresu obowiązywania – wg CRU API (inna niż data zawarcia). */
const validateCruOkresOd = (isoDate) => {
  if (!isoDate) return 'Okres od: podaj datę.';
  const date = isoToLocalDate(isoDate);
  const min = isoToLocalDate(CRU_OKRES_MIN_DATE);
  const max = isoToLocalDate(getOkresOdMaxIso());
  if (date < min) return `Okres od: nie może być wcześniejszy niż ${toCruDate(CRU_OKRES_MIN_DATE)}.`;
  if (date > max) return `Okres od: nie może być późniejszy niż 1 rok od dzisiaj (${toCruDate(getOkresOdMaxIso())}).`;
  return null;
};

const validateCruOkresDo = (isoDate, okresOd) => {
  if (!isoDate) return 'Okres do: podaj datę.';
  const date = isoToLocalDate(isoDate);
  const min = isoToLocalDate(CRU_OKRES_MIN_DATE);
  const max = isoToLocalDate(CRU_OKRES_MAX_DATE);
  if (date < min) return `Okres do: nie może być wcześniejszy niż ${toCruDate(CRU_OKRES_MIN_DATE)}.`;
  if (date > max) return `Okres do: nie może być późniejszy niż 31.12.2500.`;
  if (okresOd) {
    const od = isoToLocalDate(okresOd);
    if (date < od) return 'Okres do nie może być wcześniejszy niż okres od.';
  }
  return null;
};

const CRU_FIELD_LABELS = {
  'podstawoweDane.dataZawarciaUmowy': 'Data zawarcia umowy',
  'podstawoweDane.numerUmowy': 'Numer umowy',
  'podstawoweDane.statusUmowy': 'Status umowy',
  'szczegolyUmowy.wartoscPrzedmiotu': 'Wartość przedmiotu',
  'szczegolyUmowy.przedmiotUmowy': 'Przedmiot umowy',
  'okresObowiazywania.okresOd': 'Okres od',
  'okresObowiazywania.okresDo': 'Okres do',
  'stronyUmowy': 'Strony umowy',
};

const cruFieldLabel = (path) => CRU_FIELD_LABELS[path] || path;

/** Kolorowy badge statusu procesowego umowy. */
const StatusProcesowyBadge = ({ item }) => {
  const status = item?.statusProcesowy || '—';
  let cls = 'bg-gray-100 text-gray-700 ring-gray-200';
  if (item?.superseded) {
    cls = 'bg-slate-100 text-slate-500 ring-slate-200 line-through decoration-slate-300';
  } else if (canPublishStatus(item)) {
    cls = 'bg-amber-100 text-amber-800 ring-amber-200';
  } else if (/opublikowana/i.test(status)) {
    cls = 'bg-emerald-100 text-emerald-800 ring-emerald-200';
  } else if (/wycofana/i.test(status)) {
    cls = 'bg-rose-100 text-rose-800 ring-rose-200';
  } else if (/oczekuje/i.test(status)) {
    cls = 'bg-amber-100 text-amber-800 ring-amber-200';
  } else if (/weryfik/i.test(status)) {
    cls = 'bg-blue-100 text-blue-800 ring-blue-200';
  }
  return (
    <span
      className={`inline-flex max-w-full items-center truncate rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}
      title={item?.publishBlockedReason || status}
    >
      {status}
    </span>
  );
};

const canPublishStatus = (item) =>
  Boolean(item?.draftId) || item?.source === 'CRM_DRAFT' || item?.canPublishFromCrm === true;

/** Badge statusu umowy (Aktywna / Nieaktywna). */
const StatusUmowyBadge = ({ value }) => {
  if (!value) return <span className="text-gray-400">—</span>;
  const active = /aktywna/i.test(value) && !/nieaktywna/i.test(value);
  const cls = active
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : 'bg-gray-100 text-gray-600 ring-gray-200';
  return (
    <span className={`inline-flex max-w-full items-center truncate rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}>
      {value}
    </span>
  );
};

const ACTION_VARIANTS = {
  primary: 'text-white bg-emerald-600 hover:bg-emerald-700 ring-0',
  indigo: 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100 ring-1 ring-inset ring-indigo-100',
  neutral: 'text-primary-700 bg-primary-50 hover:bg-primary-100 ring-1 ring-inset ring-primary-100',
  danger: 'text-rose-700 bg-rose-50 hover:bg-rose-100 ring-1 ring-inset ring-rose-100',
  success: 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 ring-1 ring-inset ring-emerald-100',
  ghost: 'text-gray-600 bg-white hover:bg-gray-100 ring-1 ring-inset ring-gray-200',
};

/** Kompaktowy przycisk akcji w tabeli (ikona + opcjonalna etykieta). */
const ActionButton = ({ onClick, icon: Icon, label, variant = 'ghost', title, disabled, loading, iconOnly = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title || label}
    className={`inline-flex items-center gap-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      iconOnly ? 'p-1.5' : 'px-2.5 py-1.5'
    } ${ACTION_VARIANTS[variant] || ACTION_VARIANTS.ghost}`}
  >
    {loading ? <LoadingSpinner size="sm" /> : <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
    {!iconOnly && <span className="hidden lg:inline">{label}</span>}
  </button>
);

const CruResultPanel = ({ result }) => {
  if (!result) return null;

  const errorCount = result.errorCount ?? 0;
  const successCount = result.successCount ?? 0;
  const errors = result.errors ? Object.values(result.errors) : [];

  return (
    <div className={`p-4 rounded-md border ${errorCount > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
      <p className={`text-sm font-semibold ${errorCount > 0 ? 'text-red-900' : 'text-green-900'}`}>
        {result.message || (errorCount > 0 ? 'CRU zgłosiło błędy' : 'Operacja zakończona pomyślnie')}
      </p>
      {successCount > 0 && (
        <p className="text-xs text-green-800 mt-1">Pomyślnie: {successCount}</p>
      )}
      {errors.length > 0 && (
        <ul className="mt-3 space-y-2">
          {errors.map((err, idx) => (
            <li key={idx} className="text-sm text-red-800 bg-white/70 rounded p-2 border border-red-100">
              {err.message && <p className="font-medium">{err.message}</p>}
              {err.fields && Object.entries(err.fields).map(([field, msg]) => (
                <p key={field} className="mt-1 text-xs">
                  <span className="font-semibold">{cruFieldLabel(field)}:</span> {msg}
                </p>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const emptyParty = () => ({
  rodzaj: 'SU01',
  kraj: 'PL',
  czyKonsorcjum: false,
  nazwa: '',
  regon: '',
  nip: '',
  imie: '',
  nazwisko: '',
  niejawnosc: emptyNiejawnosc(),
  lookupName: '',
  lookupStatus: '',
  lookupAddress: '',
  lookupSource: '',
  lookupInfo: '',
  lookupCandidates: [],
  selectedCandidateRegon: '',
  lookupLoading: false,
  lookupError: '',
});

const normalizePartyDigits = (value) => (value || '').replace(/\D/g, '');

const partyLookupKey = (party) => {
  const nip = normalizePartyDigits(party?.nip);
  const regon = normalizePartyDigits(party?.regon);
  if (nip.length === 10) return `nip:${nip}`;
  if (regon.length === 9 || regon.length === 14) return `regon:${regon}`;
  return null;
};

/** Czy wybrana działalność wymaga publikacji w przeglądarce CRU (API nie przyjmie strony umowy). */
const partyRequiresCruBrowser = (party) => {
  if (!party || party.lookupCandidates?.length <= 1) return false;
  const selected = party.selectedCandidateRegon || party.regon;
  if (!selected) return false;
  const candidate = party.lookupCandidates.find((c) => c.regon === selected);
  if (candidate?.cruApiSupported === false) return true;
  return normalizePartyDigits(selected).length !== 14;
};

const applyCandidateSelection = (party, candidate, nipFallback) => {
  if (!candidate?.regon) return party;
  const useRegon14 = normalizePartyDigits(candidate.regon).length === 14;
  const apiSupported = candidate.cruApiSupported !== false && useRegon14;
  return {
    ...party,
    selectedCandidateRegon: candidate.regon,
    regon: candidate.regon,
    nip: useRegon14 ? '' : '',
    lookupName: candidate.name || '',
    lookupAddress: candidate.address || '',
    lookupSource: 'GUS_REGON',
    lookupInfo: '',
    lookupError: apiSupported
      ? ''
      : 'Ta działalność (CEIDG) nie ma 14-cyfrowego REGON w rejestrze — opublikuj umowę w przeglądarce CRU, nie przez „Opublikuj od razu”.',
  };
};
const canLookupParty = (party) => {
  if (!party || party.niejawnosc?.on || party.rodzaj !== 'SU01') return false;
  if ((party.kraj || 'PL').trim().toUpperCase() !== 'PL') return false;
  return partyLookupKey(party) != null;
};

/** Uzupełnia nazwę kontrahenta, gdy lista ma placeholder „NIP …” / „REGON …”. */
const resolveStronaPlaceholders = async (items) => Promise.all(items.map(async (it) => {
  const label = (it.stronaUmowy || '').trim();
  if (!label.startsWith('NIP ') && !label.startsWith('REGON ')) return it;
  try {
    const params = label.startsWith('NIP ')
      ? { nip: label.slice(4).replace(/\D/g, '') }
      : { regon: label.slice(7).replace(/\D/g, '') };
    const res = await api.get('/cru/party-lookup', { params });
    if (res.data?.found !== false && res.data?.name) {
      return { ...it, stronaUmowy: res.data.name };
    }
  } catch {
    // zostaw placeholder
  }
  return it;
}));

/** Nazwa kontrahenta do wyświetlenia na liście (z lookupu MF lub pól formularza). */
const extractContractorDisplayName = (parties) => {
  for (const party of parties) {
    if (party.niejawnosc?.on) continue;
    if (party.rodzaj === 'SU02') {
      const full = `${party.imie || ''} ${party.nazwisko || ''}`.trim();
      if (full) return full;
      continue;
    }
    const name = (party.lookupName || party.nazwa || '').trim();
    if (name) return name;
  }
  return null;
};

/** Nowoczesny przełącznik (switch). */
const Toggle = ({ checked, onChange, label, description }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="flex items-start gap-3 text-left w-full group"
  >
    <span
      className={`mt-0.5 relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-primary-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </span>
    <span className="flex-1">
      <span className="block text-sm font-medium text-gray-800">{label}</span>
      {description && <span className="block text-xs text-gray-500">{description}</span>}
    </span>
  </button>
);

/** Karta sekcji formularza z ikoną i opisem. */
const SectionCard = ({ icon: Icon, title, description, children }) => (
  <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
    <div className="flex items-start gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
      {Icon && (
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
          <Icon className="h-4 w-4" />
        </span>
      )}
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
    </div>
    <div className="p-4 space-y-4">{children}</div>
  </section>
);

/** Blok wyłączenia jawności (przedmiot / wartość / strona). */
const NiejawnoscField = ({ label, description, value, onChange }) => {
  const set = (field, v) => onChange({ ...value, [field]: v });
  return (
    <div className={`rounded-lg border p-3 transition-colors ${value.on ? 'border-amber-300 bg-amber-50/60' : 'border-gray-200'}`}>
      <Toggle
        checked={value.on}
        onChange={(v) => set('on', v)}
        label={label}
        description={description}
      />
      {value.on && (
        <div className="mt-3 space-y-3 pl-12">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Podstawa prawna *</label>
            <select value={value.podstawa} onChange={(e) => set('podstawa', e.target.value)} className="inp">
              {PODSTAWA_NIEJAWNOSCI.map((p) => (
                <option key={p.code} value={p.code}>{p.label}</option>
              ))}
            </select>
          </div>
          {value.podstawa === 'INNA' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Komentarz (uzasadnienie) *</label>
              <textarea
                value={value.komentarz}
                onChange={(e) => set('komentarz', e.target.value)}
                className="inp"
                rows={2}
                placeholder="Wymagany przy podstawie „Inna”."
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Buduje obiekt strony umowy zgodnie z CRU API v1.3+ (REGON/NIP z rejestru dla PL). */
const buildPartyPayload = (party) => {
  const kraj = (party.kraj || 'PL').trim().toUpperCase();
  const p = { rodzaj: party.rodzaj, kraj, czyKonsorcjum: !!party.czyKonsorcjum };

  // Wyłączenie jawności strony – wg CRU dopuszczalne tylko: kraj, rodzaj, czyKonsorcjum, niejawnoscStrony
  const niejawnosc = buildNiejawnoscPayload(party.niejawnosc);
  if (niejawnosc) {
    p.niejawnoscStrony = niejawnosc;
    return p;
  }

  if (party.rodzaj === 'SU02') {
    if (party.imie.trim()) p.imie = party.imie.trim();
    if (party.nazwisko.trim()) p.nazwisko = party.nazwisko.trim();
    return p;
  }

  if (kraj === 'PL') {
    // Dla PL: NIP albo REGON — przy wielu działalnościach w REGON wysyłamy wyłącznie 14-cyfrowy REGON
    if (party.rodzaj === 'SU01') {
      const hasMultiple = (party.lookupCandidates?.length ?? 0) > 1;
      const regonDigits = normalizePartyDigits(party.selectedCandidateRegon || party.regon);

      if (regonDigits.length === 14) {
        p.regon = regonDigits;
        return p;
      }
      if (hasMultiple) {
        // CRU odrzuca NIP przy wielu podmiotach — tylko jednoznaczny REGON14
        if (regonDigits.length === 9) {
          p.regon = regonDigits;
        }
        return p;
      }
      if (party.nip.trim()) {
        p.nip = party.nip.trim();
      } else if (party.regon.trim()) {
        p.regon = normalizePartyDigits(party.regon) || party.regon.trim();
      }
    } else if (party.rodzaj === 'SU03' && party.regon.trim()) {
      p.regon = party.regon.trim();
    }
    return p;
  }

  // Strona spoza PL (SU01)
  if (party.nazwa.trim()) p.nazwa = party.nazwa.trim();
  return p;
};

const validateParties = (parties, { forApiPublish = false } = {}) => {
  if (parties.length < 1) {
    return 'Umowa musi zawierać co najmniej dwie strony: jednostkę JSFP oraz co najmniej jednego kontrahenta.';
  }
  for (let i = 0; i < parties.length; i++) {
    const party = parties[i];
    const label = `Strona ${i + 2}`;
    const kraj = (party.kraj || 'PL').trim().toUpperCase();

    const niejawnoscError = validateNiejawnosc(party.niejawnosc, `${label} (jawność)`);
    if (niejawnoscError) return niejawnoscError;

    // Gdy jawność strony jest wyłączona, CRU nie wymaga (ani nie dopuszcza) danych identyfikacyjnych
    if (party.niejawnosc?.on) {
      continue;
    }

    if (party.rodzaj === 'SU02') {
      if (!party.imie.trim() || !party.nazwisko.trim()) {
        return `${label}: podaj imię i nazwisko (osoba fizyczna SU02).`;
      }
    } else if (kraj === 'PL') {
      if (party.rodzaj === 'SU01' && !party.nip.trim() && !party.regon.trim()) {
        return `${label}: podaj NIP lub REGON przedsiębiorcy (SU01).`;
      }
      if (party.rodzaj === 'SU01' && party.lookupCandidates?.length > 1 && !party.selectedCandidateRegon) {
        return `${label}: wybierz właściwą działalność z listy (ten NIP ma kilka wpisów w REGON).`;
      }
      if (forApiPublish && party.rodzaj === 'SU01' && partyRequiresCruBrowser(party)) {
        return `${label}: wybrana działalność (np. CEIDG) nie ma 14-cyfrowego REGON — API CRU jej nie przyjmie. Zapisz szkic i opublikuj umowę w przeglądarce CRU.`;
      }
      if (party.rodzaj === 'SU03' && !party.regon.trim()) {
        return `${label}: podaj REGON jednostki JSFP (SU03).`;
      }
    } else if (party.rodzaj === 'SU01' && !party.nazwa.trim()) {
      return `${label}: podaj nazwę kontrahenta spoza Polski.`;
    }
  }
  return null;
};

// Konwersja z formatu CRU (dd.MM.yyyy) na input date (yyyy-mm-dd)
const fromCruDate = (cruDate) => {
  if (!cruDate) return '';
  const parts = String(cruDate).split('.');
  if (parts.length !== 3) return '';
  const [d, m, y] = parts;
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

const parseNiejawnoscFromPayload = (n) => {
  if (!n || typeof n !== 'object') return emptyNiejawnosc();
  return {
    on: true,
    podstawa: n.podstawa || 'ART_5_UST_2',
    komentarz: n.komentarz || '',
  };
};

const parsePartyFromPayload = (strona) => {
  const p = emptyParty();
  p.rodzaj = strona.rodzaj || 'SU01';
  p.kraj = strona.kraj || 'PL';
  p.czyKonsorcjum = !!strona.czyKonsorcjum;
  if (strona.niejawnoscStrony) {
    p.niejawnosc = parseNiejawnoscFromPayload(strona.niejawnoscStrony);
    return p;
  }
  p.nazwa = strona.nazwa || '';
  p.regon = strona.regon || '';
  p.nip = strona.nip || '';
  p.imie = strona.imie || '';
  p.nazwisko = strona.nazwisko || '';
  if (p.nazwa.trim()) {
    p.lookupName = p.nazwa.trim();
  }
  return p;
};

/** Odtwarza stan formularza z zapisanego payloadu szkicu CRM. */
const parseDraftElementToForm = (element) => {
  const podstawowe = element?.podstawoweDane || {};
  const okres = element?.okresObowiazywania || {};
  const szczegoly = element?.szczegolyUmowy || {};
  const strony = Array.isArray(element?.stronyUmowy) ? element.stronyUmowy : [];

  const form = {
    agreementId: element?.agreementId || '',
    externalSystemId: element?.externalSystemId || '',
    komorkaWewnetrznaJsfp: element?.komorkaWewnetrznaJsfp || '',
    finansowanaZeSrodkow: !!element?.finansowanaZeSrodkow,
    statusUmowy: podstawowe.statusUmowy || 'Aktywna',
    numerUmowy: podstawowe.numerUmowy || '',
    brakNumeruUmowy: !!podstawowe.brakNumeruUmowy,
    dataZawarciaUmowy: fromCruDate(podstawowe.dataZawarciaUmowy),
    umowaNaCzasNieoznaczony: !!okres.umowaNaCzasNieoznaczony,
    okresMetoda: okres.okresOd || okres.okresDo ? 'daty' : 'liczba',
    okresLiczba: okres.okresLiczba != null ? String(okres.okresLiczba) : '',
    okresJednostka: okres.okresJednostka || 'dzień',
    okresOd: fromCruDate(okres.okresOd),
    okresDo: fromCruDate(okres.okresDo),
    przedmiotUmowy: szczegoly.przedmiotUmowy || '',
    wartoscPrzedmiotu: szczegoly.wartoscPrzedmiotu || '',
    opisWartosciPrzedmiotu: szczegoly.opisWartosciPrzedmiotu || '',
    niejawnoscPrzedmiotu: szczegoly.niejawnoscPrzedmiotu
      ? parseNiejawnoscFromPayload(szczegoly.niejawnoscPrzedmiotu)
      : emptyNiejawnosc(),
    niejawnoscWartosci: szczegoly.niejawnoscWartosciPrzedmiotu
      ? parseNiejawnoscFromPayload(szczegoly.niejawnoscWartosciPrzedmiotu)
      : emptyNiejawnosc(),
  };

  const parties = [];
  strony.forEach((strona, idx) => {
    if (idx === 0 && strona.rodzaj === 'SU03') return;
    parties.push(parsePartyFromPayload(strona));
  });
  if (parties.length === 0) parties.push(emptyParty());

  const savedContractorName = (element?._crmStronaUmowy || '').trim();
  if (savedContractorName) {
    const firstContractor = parties.find((p) => !p.niejawnosc?.on);
    if (firstContractor && !firstContractor.lookupName) {
      firstContractor.lookupName = savedContractorName;
      if (!firstContractor.nazwa) firstContractor.nazwa = savedContractorName;
    }
  }

  const changes = (element?.zmianyUmowy || []).map((c) => ({
    rodzajZmiany: c.rodzajZmiany || 'TSU02',
    dataZmiany: fromCruDate(c.dataZmiany),
    komentarz: c.komentarz || '',
  }));

  return { form, parties, changes };
};

/** Dane formularza aktualizacji opublikowanej umowy (bez historycznych zmian). */
const parseUpdateFormPayload = (element, meta = {}) => {
  const parsed = parseDraftElementToForm(element);
  parsed.form.agreementId = meta.agreementId || element?.agreementId || '';
  parsed.form.externalSystemId = element?.externalSystemId || '';
  parsed.form.komorkaWewnetrznaJsfp = element?.komorkaWewnetrznaJsfp || '';
  parsed.form.finansowanaZeSrodkow = !!element?.finansowanaZeSrodkow;
  parsed.changes = [{ rodzajZmiany: 'TSU10', dataZmiany: getTodayIso(), komentarz: '' }];
  return parsed;
};

const validateChangesList = (changes, { required = false } = {}) => {
  if (required && changes.length === 0) {
    return 'Dodaj co najmniej jedną zmianę (podstawę aktualizacji).';
  }
  for (let i = 0; i < changes.length; i += 1) {
    const ch = changes[i];
    if (!ch.dataZmiany) return `Zmiana ${i + 1}: podaj datę, od której obowiązuje aktualizacja.`;
    if (!ch.komentarz?.trim()) return `Zmiana ${i + 1}: podaj zwięzły opis zakresu aktualizacji.`;
    if (ch.komentarz.trim().length > 500) return `Zmiana ${i + 1}: opis może mieć maksymalnie 500 znaków.`;
  }
  return null;
};

const parseCruErrorMessage = (error) => {
  const data = error.response?.data;
  if (!data) return error.message || 'Błąd połączenia z CRU';
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed.message || parsed.error || data;
    } catch {
      return data;
    }
  }
  return data.message || data.error || 'Błąd CRU';
};

/** Pierwszy komunikat błędu z odpowiedzi CRU (success/errorCount/errors). */
const extractFirstCruError = (data) => {
  if (!data) return null;
  if (data.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors)[0];
    if (first?.fields && typeof first.fields === 'object') {
      const fieldMsg = Object.values(first.fields)[0];
      if (fieldMsg) return String(fieldMsg);
    }
    if (first?.message && first.message !== 'Wystąpiły błędy walidacji.') {
      return first.message;
    }
    if (first?.message) return first.message;
  }
  return data.message || null;
};

const UmowyCru = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Lista streszczeń
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  // CRU nie zawsze zwraca licznik wszystkich umów — wtedy nie znamy liczby stron
  const [totalKnown, setTotalKnown] = useState(false);
  const [cruPageCount, setCruPageCount] = useState(0);
  // Liczniki umów w nagłówku
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [fillingStatuses, setFillingStatuses] = useState(false);
  const [fillRemaining, setFillRemaining] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    numerUmowy: '',
    przedmiotUmowy: '',
    dataZawarciaUmowyOd: '',
    dataZawarciaUmowyDo: '',
    sortDirection: 'DESC',
  });
  const [hideSuperseded, setHideSuperseded] = useState(true);

  // Szczegóły
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Formularz publikacji
  const [publishOpen, setPublishOpen] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [updatingAgreementId, setUpdatingAgreementId] = useState(null);
  const [publishingDraftId, setPublishingDraftId] = useState(null);
  const [publishingAgreementId, setPublishingAgreementId] = useState(null);
  const [deletingDraftId, setDeletingDraftId] = useState(null);

  // Wycofanie z publikacji
  const [withdrawItem, setWithdrawItem] = useState(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  // Własny klucz API CRU (per użytkownik)
  const [myKeyInput, setMyKeyInput] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [showKeyValue, setShowKeyValue] = useState(false);
  const [keyPanelOpen, setKeyPanelOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      const res = await api.get('/cru/status');
      setStatus(res.data);
    } catch (error) {
      console.error('Błąd pobierania statusu CRU', error);
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async (newOffset = 0) => {
    try {
      setLoading(true);
      const params = {
        offset: newOffset,
        limit: PAGE_SIZE,
        sortDirection: filters.sortDirection,
      };
      if (filters.numerUmowy.trim()) params.numerUmowy = filters.numerUmowy.trim();
      if (filters.przedmiotUmowy.trim()) params.przedmiotUmowy = filters.przedmiotUmowy.trim();
      if (filters.dataZawarciaUmowyOd) params.dataZawarciaUmowyOd = toCruDate(filters.dataZawarciaUmowyOd);
      if (filters.dataZawarciaUmowyDo) params.dataZawarciaUmowyDo = toCruDate(filters.dataZawarciaUmowyDo);

      const res = await api.get('/cru/agreements/summary', { params });
      const data = res.data || {};
      const content = Array.isArray(data.content) ? data.content : [];
      const withNames = await resolveStronaPlaceholders(content);
      setItems(withNames);
      // CRU API czasem nie zwraca pola total — wtedy backend odsyła totalKnown=false,
      // a o istnieniu kolejnej strony wnioskujemy z tego, czy przyszła pełna strona.
      setTotal(typeof data.total === 'number' ? data.total : content.length);
      setTotalKnown(data.totalKnown === true || (data.totalKnown === undefined && typeof data.total === 'number'));
      // Szkice CRM są doklejane po stronie backendu — nie liczą się do rozmiaru strony z CRU
      setCruPageCount(content.length - (typeof data.draftCount === 'number' ? data.draftCount : 0));
      setOffset(newOffset);
    } catch (error) {
      const msg = error.response?.data?.message || 'Błąd podczas pobierania listy umów z CRU';
      toast.error(msg);
      setItems([]);
      setTotal(0);
      setTotalKnown(false);
      setCruPageCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const res = await api.get('/cru/agreements/stats');
      setStats(res.data || null);
    } catch (error) {
      console.error('Błąd pobierania liczników CRU', error);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  /**
   * Uzupełnia brakujące statusy aktywna/nieaktywna. Backend pracuje partiami
   * (każdy status to osobne zapytanie do CRU z odstępem), więc wołamy go w pętli
   * aż do wyczerpania braków.
   */
  const fillStatuses = useCallback(async () => {
    setFillingStatuses(true);
    try {
      let remaining = 0;
      let guard = 0;
      do {
        const res = await api.post('/cru/agreements/stats/fill-statuses', null, {
          params: { max: 25 },
          timeout: 120000,
        });
        remaining = res.data?.remaining ?? 0;
        setFillRemaining(remaining);
        guard += 1;
      } while (remaining > 0 && guard < 40);

      await fetchStats();
      toast.success(
        remaining > 0
          ? `Uzupełniono część statusów, zostało ${remaining}. Kliknij ponownie, aby dokończyć.`
          : 'Statusy umów zostały uzupełnione'
      );
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error
        || 'Nie udało się uzupełnić statusów umów';
      toast.error(msg);
      await fetchStats();
    } finally {
      setFillingStatuses(false);
      setFillRemaining(0);
    }
  }, [fetchStats]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.configured) {
      fetchSummary(0);
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.configured]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSummary(0);
  };

  const openDetails = async (agreementId) => {
    if (!agreementId) {
      toast.error('Brak identyfikatora umowy (agreementId) – nie można pobrać szczegółów.');
      return;
    }
    setDetailsOpen(true);
    setDetails(null);
    setDetailsLoading(true);
    try {
      const res = await api.get(`/cru/agreements/${encodeURIComponent(agreementId)}`);
      setDetails(typeof res.data === 'string' ? JSON.parse(res.data) : res.data);
    } catch (error) {
      toast.error(parseCruErrorMessage(error));
      setDetailsOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const publishDraft = async (draftId) => {
    if (!draftId) return;
    if (!window.confirm('Wysłać ten szkic do publikacji w CRU? Operacja wyśle dane przez API i utworzy opublikowaną umowę w Centralnym Rejestrze Umów. Tej czynności nie można cofnąć jednym kliknięciem — opublikowaną umowę można później jedynie wycofać z publikacji.')) {
      return;
    }
    setPublishingDraftId(draftId);
    try {
      const res = await api.post(`/cru/drafts/${draftId}/publish`);
      const errorCount = res.data?.errorCount ?? 0;
      if (errorCount === 0) {
        if (res.data?.alreadyExisted) {
          toast.success('Ta umowa była już opublikowana w CRU — na liście widać opublikowaną wersję.');
        } else {
          toast.success('Umowa została opublikowana w CRU');
        }
        fetchSummary(offset);
      } else {
        toast.error(extractFirstCruError(res.data) || `CRU zgłosiło błędy (${errorCount}).`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Błąd publikacji szkicu w CRU');
    } finally {
      setPublishingDraftId(null);
    }
  };

  const deleteDraft = async (draftId) => {
    if (!draftId) return;
    if (!window.confirm('Usunąć ten szkic umowy? Szkic nie został jeszcze wysłany do CRU i zostanie trwale usunięty z CRM.')) {
      return;
    }
    setDeletingDraftId(draftId);
    try {
      await api.delete(`/cru/drafts/${draftId}`);
      toast.success('Szkic umowy został usunięty');
      fetchSummary(offset);
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Błąd usuwania szkicu umowy');
    } finally {
      setDeletingDraftId(null);
    }
  };

  const publishPendingAgreement = async (agreementId) => {
    if (!agreementId) return;
    if (!window.confirm('Opublikować tę umowę w CRU? Operacja wysyła dane przez API i tworzy opublikowaną wersję umowy.')) {
      return;
    }
    setPublishingAgreementId(agreementId);
    try {
      const res = await api.post(`/cru/agreements/${encodeURIComponent(agreementId)}/publish`);
      const errorCount = res.data?.errorCount ?? 0;
      if (errorCount === 0) {
        if (res.data?.alreadyExisted) {
          toast.success('Ta umowa była już opublikowana w CRU — na liście widać opublikowaną wersję.');
        } else {
          toast.success('Umowa została opublikowana w CRU');
        }
        fetchSummary(offset);
      } else {
        toast.error(extractFirstCruError(res.data) || `CRU zgłosiło błędy (${errorCount}).`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Błąd publikacji umowy w CRU');
    } finally {
      setPublishingAgreementId(null);
    }
  };

  const isCrmDraft = (item) => Boolean(item?.draftId) || item?.source === 'CRM_DRAFT';
  const canModifyCruItem = (item) => {
    if (item?.canModifyFromCrm === true) return true;
    if (item?.canModifyFromCrm === false) return false;
    if (!user) return false;
    if (user.role === 'ADMIN' || user.role === 'SUPERADMIN') return true;
    const ownerId = item?.wprowadzilUserId;
    return ownerId != null && Number(ownerId) === Number(user.id);
  };
  const canPublishFromCrm = (item) => (isCrmDraft(item) || item?.canPublishFromCrm === true) && canModifyCruItem(item);
  const canUpdateFromCrm = (item) => !isCrmDraft(item) && item?.canUpdateFromCrm === true && Boolean(item?.agreementId) && canModifyCruItem(item);
  const canWithdrawFromCrm = (item) => !isCrmDraft(item) && item?.canWithdrawFromCrm === true && Boolean(item?.agreementId) && canModifyCruItem(item);

  const openWithdraw = (item) => {
    setWithdrawReason('');
    setWithdrawItem(item);
  };

  const confirmWithdraw = async () => {
    if (!withdrawItem?.agreementId) return;
    if (!withdrawReason.trim()) {
      toast.error('Podaj uzasadnienie wycofania umowy z publikacji.');
      return;
    }
    setWithdrawing(true);
    try {
      const res = await api.post(
        `/cru/agreements/${encodeURIComponent(withdrawItem.agreementId)}/withdraw`,
        { komentarz: withdrawReason.trim() }
      );
      const errorCount = res.data?.errorCount ?? 0;
      if (errorCount === 0) {
        toast.success('Umowa została wycofana z publikacji w CRU');
        setWithdrawItem(null);
        setWithdrawReason('');
        fetchSummary(offset);
      } else {
        toast.error(extractFirstCruError(res.data) || `CRU zgłosiło błędy (${errorCount}).`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Błąd wycofania umowy z publikacji');
    } finally {
      setWithdrawing(false);
    }
  };

  const saveMyKey = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!myKeyInput.trim()) {
      toast.error('Wprowadź swój klucz API CRU.');
      return;
    }
    setSavingKey(true);
    try {
      await api.put('/cru/my-key', { apiKey: myKeyInput.trim() });
      toast.success('Twój klucz API CRU został zapisany');
      setMyKeyInput('');
      setShowKeyValue(false);
      setKeyPanelOpen(false);
      await fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Nie udało się zapisać klucza API CRU');
    } finally {
      setSavingKey(false);
    }
  };

  const deleteMyKey = async () => {
    if (!window.confirm('Usunąć swój klucz API CRU? Do czasu wprowadzenia nowego klucza nie będziesz mieć dostępu do operacji CRU.')) {
      return;
    }
    setSavingKey(true);
    try {
      await api.delete('/cru/my-key');
      toast.success('Twój klucz API CRU został usunięty');
      setKeyPanelOpen(false);
      await fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Nie udało się usunąć klucza API CRU');
    } finally {
      setSavingKey(false);
    }
  };

  const openNewAgreementForm = () => {
    setEditingDraftId(null);
    setUpdatingAgreementId(null);
    setPublishOpen(true);
  };

  const openDraftEdit = (draftId) => {
    setUpdatingAgreementId(null);
    setEditingDraftId(draftId);
    setPublishOpen(true);
  };

  const openAgreementUpdate = (agreementId) => {
    if (!agreementId) return;
    setEditingDraftId(null);
    setUpdatingAgreementId(agreementId);
    setPublishOpen(true);
  };

  const visibleItems = hideSuperseded
    ? items.filter((it) => !it.superseded)
    : items;

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Gdy CRU nie poda licznika wszystkich umów, o kolejnej stronie wnioskujemy z pełnej strony
  const hasNextPage = totalKnown ? currentPage < totalPages : cruPageCount >= PAGE_SIZE;

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center">
            <ScrollText className="h-6 w-6 text-primary-600 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">Umowy CRU</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchSummary(offset)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={!status?.configured || loading}
            >
              <RefreshCw className="h-4 w-4 mr-1" /> Odśwież
            </button>
            <button
              onClick={openNewAgreementForm}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
              disabled={!status?.configured}
            >
              <Plus className="h-4 w-4 mr-1" /> Nowa / aktualizacja umowy
            </button>
          </div>
        </div>

        <div className="card-body">
          {!status?.configured ? (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-yellow-900">
                  <p className="font-medium mb-1">Wprowadź swój klucz API CRU</p>
                  <p>
                    Aby korzystać z modułu, wprowadź własny klucz API (X-API-KEY) wygenerowany na Twoim koncie w aplikacji CRU.
                    Klucz jest przypisany do Ciebie — każdy użytkownik korzysta z własnego klucza.
                  </p>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start">
                <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-800">
                  Klucz API wygenerujesz w aplikacji CRU (rola Publikującego):{' '}
                  <strong>Moje Dane → Klucz API → Generuj klucz</strong>. Klucz jest ważny 6 miesięcy
                  i przechowywany w bazie w postaci zaszyfrowanej.
                </div>
              </div>

              <form onSubmit={saveMyKey} className="max-w-xl space-y-3">
                <label htmlFor="myCruApiKey" className="block text-sm font-medium text-gray-700">
                  <Key className="h-4 w-4 inline mr-1" />
                  Twój klucz API CRU (X-API-KEY) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showKeyValue ? 'text' : 'password'}
                    id="myCruApiKey"
                    value={myKeyInput}
                    onChange={(e) => setMyKeyInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Wklej swój klucz API z aplikacji CRU"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeyValue((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                  >
                    {showKeyValue ? 'Ukryj' : 'Pokaż'}
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={savingKey}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {savingKey ? 'Zapisywanie...' : 'Zapisz klucz API'}
                </button>
              </form>

              {status?.environment ? (
                <p className="text-xs text-gray-500">
                  Środowisko integracji: <span className="font-medium">{status.environment}</span>
                  {status.regon ? <> • REGON jednostki: <span className="font-medium">{status.regon}</span></> : null}
                </p>
              ) : null}
            </div>
          ) : (
            <>
              <div className="mb-3 space-y-2">
                <div className="text-xs text-gray-500">
                  Środowisko: <span className="font-medium">{status.environment}</span>
                  {status.regon ? <> • REGON jednostki: <span className="font-medium">{status.regon}</span></> : null}
                </div>

                <div className="rounded-lg bg-green-50 border border-green-200 p-2.5">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-xs text-green-900">
                      <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-600" />
                      <span>
                        Twój klucz API CRU jest zapisany
                        {status.apiKeyLast4 ? <> (************{status.apiKeyLast4})</> : null}.
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => { setKeyPanelOpen((v) => !v); setMyKeyInput(''); setShowKeyValue(false); }}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        <Key className="h-3.5 w-3.5 mr-1" /> Zmień klucz
                      </button>
                      <button
                        type="button"
                        onClick={deleteMyKey}
                        disabled={savingKey}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Usuń
                      </button>
                    </div>
                  </div>

                  {keyPanelOpen && (
                    <form onSubmit={saveMyKey} className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
                      <div className="relative flex-1">
                        <input
                          type={showKeyValue ? 'text' : 'password'}
                          value={myKeyInput}
                          onChange={(e) => setMyKeyInput(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Wklej nowy klucz API z aplikacji CRU"
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          onClick={() => setShowKeyValue((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xs"
                        >
                          {showKeyValue ? 'Ukryj' : 'Pokaż'}
                        </button>
                      </div>
                      <button
                        type="submit"
                        disabled={savingKey}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                      >
                        {savingKey ? 'Zapisywanie...' : 'Zapisz'}
                      </button>
                    </form>
                  )}
                </div>
                <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-xs text-slate-700">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-slate-500" />
                  <p>
                    Wszystkie operacje wykonujesz z CRM — bez logowania do aplikacji webowej CRU.
                    <strong> Przekaż do publikacji</strong> zapisuje szkic w CRM;
                    <strong> Opublikuj od razu</strong> wysyła do CRU natychmiast.
                    Umowy <strong>Robocza</strong> z CRU web nie są widoczne w API.
                    Stare wpisy „Przekazana do publikacji” po publikacji z CRM są oznaczone jako <strong>zastąpione</strong>.
                  </p>
                </div>
                {/* Liczniki umów. Total i podział na opublikowane pochodzą z CRU,
                    aktywne/nieaktywne z lokalnie zapisanych statusów (CRU nie zwraca ich w liście). */}
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-700">Liczba umów w CRU</span>
                    <button
                      onClick={fetchStats}
                      disabled={statsLoading || fillingStatuses}
                      className="text-xs text-primary-600 hover:text-primary-700 disabled:opacity-50"
                    >
                      {statsLoading ? 'Liczenie...' : 'Przelicz'}
                    </button>
                  </div>

                  {stats ? (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                        <StatTile label="Łącznie" value={stats.total} tone="slate" />
                        <StatTile label="Opublikowane" value={stats.published} tone="green" />
                        <StatTile label="Nieopublikowane" value={stats.notPublished} tone="amber" />
                        <StatTile label="Aktywne" value={stats.active} tone="blue" />
                        <StatTile label="Nieaktywne" value={stats.inactive} tone="gray" />
                        <StatTile label="Szkice w CRM" value={stats.drafts} tone="purple" />
                      </div>

                      {stats.byStatusProcesowy && Object.keys(stats.byStatusProcesowy).length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <span className="text-[11px] text-gray-500">Wg statusu procesowego:</span>
                          {Object.entries(stats.byStatusProcesowy).map(([statusName, count]) => (
                            <span
                              key={statusName}
                              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-700"
                            >
                              {statusName}: <strong>{count}</strong>
                            </span>
                          ))}
                        </div>
                      )}

                      <p className="mt-2 text-[11px] leading-snug text-gray-500">
                        Liczniki obejmują wszystko, co zwraca API CRU — także wpisy ukryte w tabeli
                        przez filtr „Ukryj zastąpione”. Aplikacja webowa CRU pokazuje zwykle więcej
                        pozycji, bo jej „Wykaz umów” zawiera też wpisy <strong>usunięte/wycofane</strong>
                        oraz o statusie <strong>Robocza</strong> — API ich nie udostępnia. Zwykle jest to
                        starsza wersja umowy, zastąpiona nowszą publikacją tego samego numeru.
                      </p>

                      {stats.unknownStatus > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                          <span>
                            Dla <strong>{stats.unknownStatus}</strong>{' '}
                            {stats.unknownStatus === 1 ? 'umowy' : 'umów'} nie pobrano jeszcze statusu
                            aktywna/nieaktywna — CRU nie podaje go na liście.
                          </span>
                          <button
                            onClick={fillStatuses}
                            disabled={fillingStatuses || statsLoading}
                            className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                          >
                            {fillingStatuses
                              ? `Uzupełnianie... (zostało ${fillRemaining})`
                              : 'Uzupełnij statusy'}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-gray-500">
                      {statsLoading ? 'Liczenie umów...' : 'Nie udało się pobrać liczników.'}
                    </p>
                  )}
                </div>

                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hideSuperseded}
                    onChange={(e) => setHideSuperseded(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Ukryj zastąpione wpisy (stare „Przekazana do publikacji” po publikacji z CRM)
                </label>
              </div>

              <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
                <input
                  type="text"
                  value={filters.numerUmowy}
                  onChange={(e) => setFilters({ ...filters, numerUmowy: e.target.value })}
                  placeholder="Numer umowy"
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="text"
                  value={filters.przedmiotUmowy}
                  onChange={(e) => setFilters({ ...filters, przedmiotUmowy: e.target.value })}
                  placeholder="Przedmiot umowy"
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Data zawarcia od</label>
                  <input
                    type="date"
                    value={filters.dataZawarciaUmowyOd}
                    onChange={(e) => setFilters({ ...filters, dataZawarciaUmowyOd: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Data zawarcia do</label>
                  <input
                    type="date"
                    value={filters.dataZawarciaUmowyDo}
                    onChange={(e) => setFilters({ ...filters, dataZawarciaUmowyDo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <select
                    value={filters.sortDirection}
                    onChange={(e) => setFilters({ ...filters, sortDirection: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="DESC">Malejąco</option>
                    <option value="ASC">Rosnąco</option>
                  </select>
                  <button
                    type="submit"
                    className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                  >
                    <Search className="h-4 w-4 mr-1" /> Szukaj
                  </button>
                </div>
              </form>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : visibleItems.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ScrollText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  {items.length > 0 && hideSuperseded
                    ? 'Brak umów do wyświetlenia (włączone ukrywanie zastąpionych wpisów).'
                    : offset > 0
                      ? 'To już koniec listy — na tej stronie nie ma umów.'
                      : 'Brak umów do wyświetlenia.'}
                  {/* Bez tego użytkownik utknąłby na pustej stronie – paginacja niżej się nie renderuje */}
                  {offset > 0 && (
                    <div className="mt-4">
                      <button
                        onClick={() => fetchSummary(Math.max(0, offset - PAGE_SIZE))}
                        disabled={loading}
                        className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4" /> Poprzednia strona
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="w-full min-w-0 overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                    <table className="w-full table-fixed divide-y divide-gray-200 text-xs">
                      <colgroup>
                        <col style={{ width: '11%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '17%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '19%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '11%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '12%' }} />
                      </colgroup>
                      <thead className="bg-gray-50/80">
                        <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                          <th className="px-2 py-2">Nr umowy</th>
                          <th className="px-2 py-2 whitespace-nowrap">Nr syst.</th>
                          <th className="px-2 py-2">Strona</th>
                          <th className="px-2 py-2 whitespace-nowrap">Data zaw.</th>
                          <th className="px-2 py-2">Przedmiot</th>
                          <th className="px-2 py-2">Status</th>
                          <th className="px-2 py-2">Proc.</th>
                          <th className="px-2 py-2 whitespace-nowrap">Wprow.</th>
                          <th className="px-2 py-2 text-right">Akcje</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {visibleItems.map((it) => (
                          <tr
                            key={it.draftId ? `draft-${it.draftId}` : (it.agreementId || it.numerSystemowy)}
                            className={`transition-colors ${it.superseded ? 'bg-slate-50/70 hover:bg-slate-100/70' : 'hover:bg-primary-50/40'}`}
                          >
                            <td className="px-2 py-2 align-middle max-w-0">
                              {it.draftId ? (
                                <span className="inline-flex items-center gap-1 min-w-0">
                                  <span className="inline-flex flex-shrink-0 items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                                    Szkic
                                  </span>
                                  <span className="truncate font-medium text-gray-900" title={it.numerUmowy || undefined}>
                                    {it.numerUmowy || '—'}
                                  </span>
                                </span>
                              ) : (
                                <span className="block truncate font-medium text-gray-900" title={it.numerUmowy || undefined}>
                                  {it.numerUmowy || '—'}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2 align-middle font-mono text-[11px] text-gray-500 whitespace-nowrap">{it.numerSystemowy ?? '—'}</td>
                            <td className="px-2 py-2 align-middle max-w-0">
                              <span className="block truncate text-gray-800" title={it.stronaUmowy || undefined}>
                                {it.stronaUmowy || '—'}
                              </span>
                            </td>
                            <td className="px-2 py-2 align-middle text-gray-600 whitespace-nowrap tabular-nums">{it.dataZawarciaUmowy || '—'}</td>
                            <td className="px-2 py-2 align-middle max-w-0 text-gray-600">
                              <span className="block truncate" title={it.przedmiotUmowy || undefined}>{it.przedmiotUmowy || '—'}</span>
                            </td>
                            <td className="px-2 py-2 align-middle max-w-0"><StatusUmowyBadge value={it.statusUmowy} /></td>
                            <td className="px-2 py-2 align-middle max-w-0"><StatusProcesowyBadge item={it} /></td>
                            <td className="px-2 py-2 align-middle max-w-0 text-gray-600">
                              <span className="block truncate" title={it.wprowadzil || undefined}>{it.wprowadzil || '—'}</span>
                            </td>
                            <td className="px-2 py-2 align-middle">
                              <div className="flex items-center justify-end gap-0.5 flex-wrap">
                                {it.superseded && it.publishedReplacementAgreementId && (
                                  <ActionButton
                                    onClick={() => openDetails(it.publishedReplacementAgreementId)}
                                    icon={Eye}
                                    label="Wersja aktywna"
                                    variant="success"
                                    iconOnly
                                    title={`Wersja aktywna (nr systemowy ${it.publishedReplacementNumerSystemowy || '—'})`}
                                  />
                                )}
                                {isCrmDraft(it) && canModifyCruItem(it) && (
                                  <ActionButton onClick={() => openDraftEdit(it.draftId)} icon={FileText} label="Edytuj" variant="neutral" iconOnly title="Podgląd i edycja szkicu przed publikacją" />
                                )}
                                {isCrmDraft(it) && canModifyCruItem(it) && (
                                  <ActionButton
                                    onClick={() => deleteDraft(it.draftId)}
                                    disabled={deletingDraftId === it.draftId}
                                    loading={deletingDraftId === it.draftId}
                                    icon={Trash2}
                                    label="Usuń"
                                    variant="danger"
                                    iconOnly
                                    title="Usuń szkic (jeszcze niewysłany do CRU)"
                                  />
                                )}
                                {canPublishFromCrm(it) && (
                                  <ActionButton
                                    onClick={() => (isCrmDraft(it) ? publishDraft(it.draftId) : publishPendingAgreement(it.agreementId))}
                                    disabled={(isCrmDraft(it) && publishingDraftId === it.draftId) || (!isCrmDraft(it) && publishingAgreementId === it.agreementId)}
                                    loading={(isCrmDraft(it) && publishingDraftId === it.draftId) || (!isCrmDraft(it) && publishingAgreementId === it.agreementId)}
                                    icon={Upload}
                                    label="Opublikuj"
                                    variant="primary"
                                    iconOnly
                                    title={isCrmDraft(it) ? 'Wyślij zapisany szkic do CRU' : 'Opublikuj umowę w CRU przez API'}
                                  />
                                )}
                                {canUpdateFromCrm(it) && (
                                  <ActionButton onClick={() => openAgreementUpdate(it.agreementId)} icon={PencilLine} label="Aktualizuj" variant="indigo" iconOnly title="Aktualizacja opublikowanej umowy w CRU" />
                                )}
                                {canWithdrawFromCrm(it) && (
                                  <ActionButton onClick={() => openWithdraw(it)} icon={Undo2} label="Wycofaj" variant="danger" iconOnly title="Wycofaj umowę z publikacji w CRU" />
                                )}
                                {!it.draftId && (
                                  <ActionButton
                                    onClick={() => openDetails(it.agreementId)}
                                    disabled={!it.agreementId}
                                    icon={Eye}
                                    label="Szczegóły"
                                    variant="ghost"
                                    iconOnly
                                    title={it.agreementId ? `Szczegóły umowy (${it.agreementId})` : 'Brak identyfikatora – odśwież listę'}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
                    <p className="text-sm text-gray-500">
                      {totalKnown ? (
                        <>Łącznie: <span className="font-medium text-gray-700">{total}</span></>
                      ) : (
                        <>Na tej stronie: <span className="font-medium text-gray-700">{items.length}</span></>
                      )}
                      {hideSuperseded && items.length !== visibleItems.length ? ` • widocznych: ${visibleItems.length}` : ''}
                      {totalKnown ? ` • Strona ${currentPage} z ${totalPages}` : ` • Strona ${currentPage}`}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => fetchSummary(Math.max(0, offset - PAGE_SIZE))}
                        disabled={offset === 0 || loading}
                        className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4" /> Poprzednia
                      </button>
                      <button
                        onClick={() => fetchSummary(offset + PAGE_SIZE)}
                        disabled={!hasNextPage || loading}
                        className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Następna <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {detailsOpen && (
        <DetailsModal
          loading={detailsLoading}
          details={details}
          onClose={() => setDetailsOpen(false)}
        />
      )}

      {publishOpen && (
        <PublishModal
          draftId={editingDraftId}
          updateAgreementId={updatingAgreementId}
          regon={status?.regon}
          webBaseUrl={status?.webBaseUrl}
          onClose={() => {
            setPublishOpen(false);
            setEditingDraftId(null);
            setUpdatingAgreementId(null);
          }}
          onPublished={() => {
            setPublishOpen(false);
            setEditingDraftId(null);
            setUpdatingAgreementId(null);
            fetchSummary(offset);
          }}
        />
      )}

      {withdrawItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                  <Undo2 className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Wycofanie z publikacji</h3>
                  <p className="text-xs text-gray-500">
                    Umowa {withdrawItem.numerUmowy || '—'} • nr systemowy {withdrawItem.numerSystemowy || '—'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setWithdrawItem(null); setWithdrawReason(''); }}
                disabled={withdrawing}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Po wycofaniu umowa w CRU przejdzie do statusu „Wycofana z publikacji”. Operacja jest możliwa tylko dla umów opublikowanych, bez rozpoczętego procesu zmiany danych.</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Uzasadnienie wycofania <span className="text-rose-500">*</span>
                </label>
                <textarea
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="np. Błędna publikacja — umowa zostanie opublikowana ponownie po korekcie danych."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
                <p className="mt-1 text-right text-xs text-gray-400">{withdrawReason.length}/500</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
              <button
                onClick={() => { setWithdrawItem(null); setWithdrawReason(''); }}
                disabled={withdrawing}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Anuluj
              </button>
              <button
                onClick={confirmWithdraw}
                disabled={withdrawing || !withdrawReason.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {withdrawing ? <LoadingSpinner size="sm" /> : <Undo2 className="h-4 w-4" />}
                Wycofaj z publikacji
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const rodzajStronyLabel = (code) => RODZAJ_STRONY.find((r) => r.code === code)?.label || code || '—';
const rodzajZmianyLabel = (code) => RODZAJ_ZMIANY.find((r) => r.code === code)?.label || code || '—';

const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Tak' : 'Nie';
  return String(value);
};

const formatAddress = (addr) => {
  if (!addr) return '—';
  if (typeof addr === 'string') return addr;
  const street = [addr.ulica, addr.numerNieruchomosci].filter(Boolean).join(' ');
  const streetFull = [street, addr.numerLokalu ? `/${addr.numerLokalu}` : ''].join('');
  return [streetFull, addr.kodPocztowy, addr.miejscowosc, addr.wojewodztwo].filter(Boolean).join(', ') || '—';
};

const formatOkresObowiazywania = (okres) => {
  if (!okres) return '—';
  if (okres.okresPrezentacja) return okres.okresPrezentacja;
  if (okres.umowaNaCzasNieoznaczony) return 'Umowa na czas nieoznaczony';
  if (okres.okresLiczba != null && okres.okresJednostka) {
    return `${okres.okresLiczba} ${okres.okresJednostka}`;
  }
  if (okres.okresOd || okres.okresDo) {
    return [okres.okresOd, okres.okresDo].filter(Boolean).join(' – ');
  }
  return '—';
};

const formatNiejawnosc = (n) => {
  if (!n) return 'Jawne';
  const podstawa = podstawaNiejawnosciLabel(n.podstawa);
  if (n.komentarz) return `Wyłączona — ${podstawa} (${n.komentarz})`;
  return `Wyłączona — ${podstawa}`;
};

const DetailRow = ({ label, value, wide }) => (
  <div className={wide ? 'sm:col-span-2' : ''}>
    <dt className="text-xs font-medium text-gray-500">{label}</dt>
    <dd className="mt-0.5 text-sm text-gray-900 break-words">{formatDisplayValue(value)}</dd>
  </div>
);

const DetailSection = ({ title, children }) => (
  <section className="border border-gray-200 rounded-lg overflow-hidden">
    <h4 className="px-4 py-2.5 bg-gray-50 text-sm font-semibold text-gray-800 border-b border-gray-200">{title}</h4>
    <dl className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">{children}</dl>
  </section>
);

const DetailsModal = ({ loading, details, onClose }) => {
  const podstawowe = details?.podstawoweDane || {};
  const okres = details?.okresObowiazywania || {};
  const szczegoly = details?.szczegolyUmowy || {};
  const strony = Array.isArray(details?.stronyUmowy) ? details.stronyUmowy : [];
  const zmiany = Array.isArray(details?.zmianyUmowy) ? details.zmianyUmowy : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Szczegóły umowy</h3>
            {!loading && details && (
              <p className="text-xs text-gray-500 mt-0.5">
                {podstawowe.numerUmowy ? `Nr ${podstawowe.numerUmowy}` : 'Bez numeru umowy'}
                {details.statusProcesowy ? ` • ${details.statusProcesowy}` : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <LoadingSpinner size="lg" />
            </div>
          ) : !details ? (
            <p className="text-center text-gray-500 py-10">Brak danych umowy.</p>
          ) : (
            <>
              <DetailSection title="Identyfikacja">
                <DetailRow label="Agreement ID" value={details.agreementId} wide />
                <DetailRow label="Nr systemowy" value={details.numerSystemowy} />
                <DetailRow label="ID w systemie zewn." value={details.externalSystemId} />
                <DetailRow label="Status procesowy" value={details.statusProcesowy} />
                <DetailRow label="Wprowadził (CRM)" value={details.wprowadzil} />
                <DetailRow label="Komórka wewnętrzna JSFP" value={details.komorkaWewnetrznaJsfp} />
                <DetailRow label="Finansowana ze środków" value={details.finansowanaZeSrodkow} />
              </DetailSection>

              <DetailSection title="Dane podstawowe">
                <DetailRow label="Status umowy" value={podstawowe.statusUmowy} />
                <DetailRow label="Numer umowy" value={podstawowe.numerUmowy} />
                <DetailRow label="Brak numeru umowy" value={podstawowe.brakNumeruUmowy} />
                <DetailRow label="Data zawarcia" value={podstawowe.dataZawarciaUmowy} />
                <DetailRow label="Data zakończenia" value={podstawowe.dataZakonczeniaUmowy} />
              </DetailSection>

              <DetailSection title="Okres obowiązywania">
                <DetailRow label="Okres" value={formatOkresObowiazywania(okres)} wide />
                {!okres.okresPrezentacja && !okres.umowaNaCzasNieoznaczony && (
                  <>
                    <DetailRow label="Od" value={okres.okresOd} />
                    <DetailRow label="Do" value={okres.okresDo} />
                    <DetailRow label="Liczba" value={okres.okresLiczba} />
                    <DetailRow label="Jednostka" value={okres.okresJednostka} />
                  </>
                )}
              </DetailSection>

              <DetailSection title="Przedmiot i wartość">
                <DetailRow label="Przedmiot umowy" value={szczegoly.przedmiotUmowy} wide />
                <DetailRow label="Jawność przedmiotu" value={formatNiejawnosc(szczegoly.niejawnoscPrzedmiotu)} wide />
                <DetailRow label="Wartość przedmiotu" value={szczegoly.wartoscPrzedmiotu} />
                <DetailRow label="Opis wartości" value={szczegoly.opisWartosciPrzedmiotu} wide />
                <DetailRow label="Jawność wartości" value={formatNiejawnosc(szczegoly.niejawnoscWartosciPrzedmiotu)} wide />
              </DetailSection>

              <section className="border border-gray-200 rounded-lg overflow-hidden">
                <h4 className="px-4 py-2.5 bg-gray-50 text-sm font-semibold text-gray-800 border-b border-gray-200">
                  Strony umowy ({strony.length})
                </h4>
                <div className="p-4 space-y-3">
                  {strony.length === 0 ? (
                    <p className="text-sm text-gray-500">Brak danych o stronach.</p>
                  ) : (
                    strony.map((strona, idx) => {
                      const pola = [
                        ['Nazwa', strona.nazwa, true],
                        ['REGON', strona.regon],
                        ['NIP', strona.nip],
                        ['Imię', strona.imie],
                        ['Nazwisko', strona.nazwisko],
                        ['Kraj', strona.kraj],
                        ['Konsorcjum', strona.czyKonsorcjum != null ? (strona.czyKonsorcjum ? 'Tak' : 'Nie') : null],
                        ['Jawność strony', strona.niejawnoscStrony ? formatNiejawnosc(strona.niejawnoscStrony) : null, true],
                      ].filter(([, val]) => val !== null && val !== undefined && val !== '');

                      return (
                        <div key={idx} className="rounded-md border border-gray-100 bg-gray-50/80 p-3">
                          <p className="text-xs font-semibold text-primary-700 mb-2">
                            Strona {idx + 1}
                            {strona.rodzaj ? ` • ${rodzajStronyLabel(strona.rodzaj)}` : ''}
                          </p>
                          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                            {pola.map(([label, val, wide]) => (
                              <div key={label} className={wide ? 'sm:col-span-2' : ''}>
                                <dt className="text-xs font-medium text-gray-500">{label}</dt>
                                <dd className="mt-0.5 text-sm text-gray-900 break-words">{val}</dd>
                              </div>
                            ))}
                            {strona.daneAdresowe && (
                              <div className="sm:col-span-2">
                                <dt className="text-xs font-medium text-gray-500">Adres</dt>
                                <dd className="mt-0.5 text-sm text-gray-900">{formatAddress(strona.daneAdresowe)}</dd>
                              </div>
                            )}
                          </dl>
                        </div>
                      );
                    })
                  )}
                </div>
              </section>

              {zmiany.length > 0 && (
                <section className="border border-gray-200 rounded-lg overflow-hidden">
                  <h4 className="px-4 py-2.5 bg-gray-50 text-sm font-semibold text-gray-800 border-b border-gray-200">
                    Zmiany umowy ({zmiany.length})
                  </h4>
                  <div className="divide-y divide-gray-100">
                    {zmiany.map((zmiana, idx) => (
                      <div key={idx} className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <p className="text-xs font-medium text-gray-500">Rodzaj</p>
                          <p className="text-sm text-gray-900">
                            {rodzajZmianyLabel(zmiana.rodzajZmiany)}
                            {zmiana.rodzajZmiany ? ` (${zmiana.rodzajZmiany})` : ''}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Data zmiany</p>
                          <p className="text-sm text-gray-900">{formatDisplayValue(zmiana.dataZmiany)}</p>
                        </div>
                        <div className="sm:col-span-1">
                          <p className="text-xs font-medium text-gray-500">Komentarz</p>
                          <p className="text-sm text-gray-900 break-words">{formatDisplayValue(zmiana.komentarz)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
};

const KomorkaWewnetrznaField = ({ value, onChange }) => {
  const [komorki, setKomorki] = useState([]);
  const [loadingKomorki, setLoadingKomorki] = useState(true);
  const [savingKomorka, setSavingKomorka] = useState(false);
  const [mode, setMode] = useState('list');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/cru/komorki-wewnetrzne');
        if (!cancelled) setKomorki(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) toast.error('Nie udało się wczytać listy działów.');
      } finally {
        if (!cancelled) setLoadingKomorki(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!value) {
      if (!loadingKomorki && komorki.length === 0) {
        setMode('custom');
      } else {
        setMode('list');
      }
      return;
    }
    const match = komorki.find((k) => k.name === value);
    setMode(match ? 'list' : 'custom');
  }, [value, komorki, loadingKomorki]);

  const matched = komorki.find((k) => k.name === value);
  const trimmed = value.trim();
  const canSave = trimmed.length >= 2
    && !komorki.some((k) => k.name.toLowerCase() === trimmed.toLowerCase());

  const handleSelect = (e) => {
    const id = e.target.value;
    if (id === '__custom__') {
      setMode('custom');
      onChange('');
      return;
    }
    if (!id) {
      setMode('list');
      onChange('');
      return;
    }
    const match = komorki.find((k) => String(k.id) === id);
    if (match) {
      setMode('list');
      onChange(match.name);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSavingKomorka(true);
    try {
      const res = await api.post('/cru/komorki-wewnetrzne', { name: trimmed });
      const saved = res.data;
      setKomorki((prev) => {
        if (prev.some((k) => k.id === saved.id)) return prev;
        return [...prev, saved].sort((a, b) => a.name.localeCompare(b.name, 'pl'));
      });
      setMode('list');
      onChange(saved.name);
      toast.success('Dział zapisany — będzie dostępny przy kolejnych umowach.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się zapisać działu.');
    } finally {
      setSavingKomorka(false);
    }
  };

  const selectValue = mode === 'list' && matched ? String(matched.id) : '';

  return (
    <Field label="Dział (komórka wewnętrzna JSFP)">
      {mode === 'list' ? (
        <div className="space-y-1.5">
          <select
            value={selectValue}
            onChange={handleSelect}
            disabled={loadingKomorki}
            className="inp disabled:bg-gray-100"
          >
            <option value="">
              {loadingKomorki ? 'Wczytywanie działów…' : '— wybierz dział —'}
            </option>
            {komorki.map((k) => (
              <option key={k.id} value={k.id}>{k.name}</option>
            ))}
          </select>
          {!loadingKomorki && (
            <button
              type="button"
              onClick={() => { setMode('custom'); onChange(''); }}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-primary-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Dodaj nowy dział
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="flex gap-2">
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="inp flex-1 min-w-0"
              placeholder="np. Wydział Zamówień Publicznych"
              autoFocus
            />
            {canSave && (
              <button
                type="button"
                onClick={handleSave}
                disabled={savingKomorka}
                className="shrink-0 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
              >
                {savingKomorka ? '…' : 'Zapisz'}
              </button>
            )}
          </div>
          {komorki.length > 0 && (
            <button
              type="button"
              onClick={() => { setMode('list'); onChange(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← Wybierz z listy
            </button>
          )}
        </div>
      )}
    </Field>
  );
};

const PublishModal = ({ draftId, updateAgreementId, regon, webBaseUrl, onClose, onPublished }) => {
  const isUpdateMode = Boolean(updateAgreementId);
  const [loadingDraft, setLoadingDraft] = useState(Boolean(draftId || updateAgreementId));
  const [updateMeta, setUpdateMeta] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitMode, setSubmitMode] = useState(null);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    agreementId: '',
    externalSystemId: '',
    komorkaWewnetrznaJsfp: '',
    finansowanaZeSrodkow: false,
    statusUmowy: 'Aktywna',
    numerUmowy: '',
    brakNumeruUmowy: false,
    dataZawarciaUmowy: '',
    umowaNaCzasNieoznaczony: false,
    okresMetoda: 'liczba', // 'liczba' | 'daty'
    okresLiczba: '',
    okresJednostka: 'dzień',
    okresOd: '',
    okresDo: '',
    przedmiotUmowy: '',
    wartoscPrzedmiotu: '',
    opisWartosciPrzedmiotu: '',
    niejawnoscPrzedmiotu: emptyNiejawnosc(),
    niejawnoscWartosci: emptyNiejawnosc(),
  });

  const [parties, setParties] = useState([emptyParty()]); // co najmniej jedna strona kontrahenta (poza JSFP)
  const [changes, setChanges] = useState([]); // zmiany umowy
  const partyLookupTokens = useRef({});
  const cruBrowserPartyRequired = parties.some(partyRequiresCruBrowser);
  const cruWebUrl = webBaseUrl || 'https://jsfp.rejestrumow.gov.pl';

  useEffect(() => {
    if (!draftId && !updateAgreementId) {
      setLoadingDraft(false);
      setUpdateMeta(null);
      return;
    }
    let cancelled = false;
    setLoadingDraft(true);
    setResult(null);
    (async () => {
      try {
        if (updateAgreementId) {
          const res = await api.get(`/cru/agreements/${encodeURIComponent(updateAgreementId)}/update-form`);
          if (cancelled) return;
          const payload = res.data?.payload;
          const element = Array.isArray(payload) && payload.length > 0 ? payload[0] : null;
          if (!element) {
            toast.error('Nie udało się przygotować formularza aktualizacji.');
            onClose();
            return;
          }
          setUpdateMeta({
            agreementId: res.data?.agreementId || updateAgreementId,
            numerUmowy: res.data?.numerUmowy,
            numerSystemowy: res.data?.numerSystemowy,
            statusUmowy: res.data?.statusUmowy,
          });
          const parsed = parseUpdateFormPayload(element, res.data);
          setForm(parsed.form);
          setParties(parsed.parties);
          setChanges(parsed.changes);
          return;
        }

        const res = await api.get(`/cru/drafts/${draftId}`);
        if (cancelled) return;
        const payload = res.data?.payload;
        const element = Array.isArray(payload) && payload.length > 0 ? payload[0] : null;
        if (!element) {
          toast.error('Szkic nie zawiera danych umowy.');
          onClose();
          return;
        }
        const parsed = parseDraftElementToForm(element);
        setForm(parsed.form);
        setParties(parsed.parties);
        setChanges(parsed.changes);
      } catch (error) {
        if (!cancelled) {
          toast.error(parseCruErrorMessage(error));
          onClose();
        }
      } finally {
        if (!cancelled) setLoadingDraft(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, updateAgreementId]);

  const setField = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const addParty = () => setParties((p) => [...p, emptyParty()]);
  const removeParty = (idx) =>
    setParties((p) => (p.length <= 1 ? p : p.filter((_, i) => i !== idx)));
  const setParty = (idx, field, value) =>
    setParties((p) => p.map((party, i) => {
      if (i !== idx) return party;
      const next = { ...party, [field]: value };
      if (field === 'nip' || field === 'regon' || field === 'rodzaj' || field === 'kraj') {
        next.lookupName = '';
        next.lookupStatus = '';
        next.lookupAddress = '';
        next.lookupError = '';
        next.lookupInfo = '';
        next.lookupCandidates = [];
        next.selectedCandidateRegon = '';
        next.lookupLoading = false;
        partyLookupTokens.current[idx] = null;
        // Po edycji NIP/REGON czyścimy drugi identyfikator z podglądu — inaczej auto-wyszukiwanie przywraca stary NIP
        if (field === 'nip') next.regon = '';
        if (field === 'regon') next.nip = '';
      }
      return next;
    }));

  const applyPartyLookupResult = (idx, data, lookupKey) => {
    setParties((p) => p.map((party, i) => {
      if (i !== idx) return party;
      if (partyLookupTokens.current[idx] !== lookupKey) return party;
      if (partyLookupKey(party) !== lookupKey) return party;
      if (data.multiple && Array.isArray(data.candidates) && data.candidates.length > 1) {
        const sortedCandidates = [...data.candidates].sort((a, b) => {
          const aName = (a.name || '').toLowerCase();
          const bName = (b.name || '').toLowerCase();
          return aName.localeCompare(bName, 'pl');
        });
        return {
          ...party,
          nip: data.nip || party.nip,
          lookupName: '',
          lookupStatus: '',
          lookupAddress: '',
          lookupSource: 'GUS_REGON',
          lookupInfo: data.hint || 'Wybierz właściwą działalność z listy poniżej.',
          lookupCandidates: sortedCandidates,
          selectedCandidateRegon: party.selectedCandidateRegon || '',
          lookupLoading: false,
          lookupError: '',
        };
      }
      return {
        ...party,
        nip: data.nip || party.nip,
        regon: data.regon || party.regon,
        nazwa: data.name || party.nazwa,
        lookupName: data.name || '',
        lookupStatus: data.statusVat || '',
        lookupAddress: data.address || '',
        lookupSource: data.source || '',
        lookupInfo: '',
        lookupCandidates: [],
        selectedCandidateRegon: '',
        lookupLoading: false,
        lookupError: '',
      };
    }));
  };

  const selectPartyCandidate = (idx, candidate) => {
    if (!candidate?.regon) return;
    setParties((p) => p.map((party, i) => {
      if (i !== idx) return party;
      return applyCandidateSelection(party, candidate, party.nip);
    }));
  };

  const lookupPartyById = useCallback(async (idx, party) => {
    const lookupKey = partyLookupKey(party);
    if (!lookupKey || !canLookupParty(party)) return;

    partyLookupTokens.current[idx] = lookupKey;
    setParties((p) => p.map((item, i) => (i === idx ? { ...item, lookupLoading: true, lookupError: '' } : item)));

    try {
      const nip = normalizePartyDigits(party.nip);
      const regon = normalizePartyDigits(party.regon);
      const params = nip.length === 10 ? { nip } : { regon };
      const res = await api.get('/cru/party-lookup', { params });
      if (partyLookupTokens.current[idx] !== lookupKey) return;
      const data = res.data || {};
      if (data.found === false) {
        setParties((p) => p.map((item, i) => (i === idx
          ? {
              ...item,
              lookupLoading: false,
              lookupError: '',
              lookupName: '',
              lookupCandidates: [],
              selectedCandidateRegon: '',
              lookupInfo: data.hint || data.error || 'Brak podglądu w rejestrze — możesz wysłać umowę z samym NIP/REGON.',
            }
          : item)));
        return;
      }
      applyPartyLookupResult(idx, data, lookupKey);
    } catch (error) {
      if (partyLookupTokens.current[idx] !== lookupKey) return;
      const msg = error.response?.data?.error || 'Nie udało się pobrać podglądu kontrahenta.';
      setParties((p) => p.map((item, i) => (i === idx
        ? {
            ...item,
            lookupLoading: false,
            lookupError: '',
            lookupInfo: `${msg} Możesz i tak wysłać umowę — CRU zweryfikuje dane przy publikacji.`,
          }
        : item)));
    }
  }, []);

  useEffect(() => {
    if (loadingDraft) return;
    parties.forEach((party, idx) => {
      if (canLookupParty(party) && !party.lookupName && !party.lookupLoading) {
        lookupPartyById(idx, party);
      }
    });
    // Tylko po załadowaniu szkicu/aktualizacji — nie reaguj na każdą zmianę NIP podczas edycji
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingDraft]);

  const addChange = () => setChanges((c) => [...c, { rodzajZmiany: 'TSU02', dataZmiany: '', komentarz: '' }]);
  const removeChange = (idx) => setChanges((c) => c.filter((_, i) => i !== idx));
  const setChange = (idx, field, value) =>
    setChanges((c) => c.map((ch, i) => (i === idx ? { ...ch, [field]: value } : ch)));

  const buildPayload = (wartoscFormatted) => {
    const podstawoweDane = {
      statusUmowy: form.statusUmowy,
      brakNumeruUmowy: form.brakNumeruUmowy,
      dataZawarciaUmowy: toCruDate(form.dataZawarciaUmowy),
    };
    if (!form.brakNumeruUmowy && form.numerUmowy.trim()) {
      podstawoweDane.numerUmowy = form.numerUmowy.trim();
    }

    const okresObowiazywania = { umowaNaCzasNieoznaczony: form.umowaNaCzasNieoznaczony };
    if (!form.umowaNaCzasNieoznaczony) {
      if (form.okresMetoda === 'liczba') {
        okresObowiazywania.okresLiczba = form.okresLiczba !== '' ? Number(form.okresLiczba) : null;
        okresObowiazywania.okresJednostka = form.okresJednostka;
      } else {
        okresObowiazywania.okresOd = toCruDate(form.okresOd);
        okresObowiazywania.okresDo = toCruDate(form.okresDo);
      }
    }

    const szczegolyUmowy = {};
    if (form.przedmiotUmowy.trim()) szczegolyUmowy.przedmiotUmowy = form.przedmiotUmowy.trim();
    if (wartoscFormatted) szczegolyUmowy.wartoscPrzedmiotu = wartoscFormatted;
    if (form.opisWartosciPrzedmiotu.trim()) szczegolyUmowy.opisWartosciPrzedmiotu = form.opisWartosciPrzedmiotu.trim();
    const niejawnoscPrzedmiotu = buildNiejawnoscPayload(form.niejawnoscPrzedmiotu);
    if (niejawnoscPrzedmiotu) szczegolyUmowy.niejawnoscPrzedmiotu = niejawnoscPrzedmiotu;
    const niejawnoscWartosci = buildNiejawnoscPayload(form.niejawnoscWartosci);
    if (niejawnoscWartosci) szczegolyUmowy.niejawnoscWartosciPrzedmiotu = niejawnoscWartosci;

    // Pierwsza strona = jednostka JSFP (REGON z konfiguracji) – wg CRU API v1.3+ tylko rodzaj + regon
    const stronyUmowy = [{ rodzaj: 'SU03', regon: regon || '' }];
    parties.forEach((party) => {
      stronyUmowy.push(buildPartyPayload(party));
    });

    const element = {
      numerPorzadkowy: 1,
      komorkaWewnetrznaJsfp: form.komorkaWewnetrznaJsfp.trim() || undefined,
      finansowanaZeSrodkow: form.finansowanaZeSrodkow,
      podstawoweDane,
      okresObowiazywania,
      szczegolyUmowy,
      stronyUmowy,
    };

    const contractorName = extractContractorDisplayName(parties);
    if (contractorName) {
      element._crmStronaUmowy = contractorName;
    }

    if (updateAgreementId || form.agreementId.trim()) {
      element.agreementId = (updateAgreementId || form.agreementId.trim());
    }
    if (form.externalSystemId.trim()) {
      element.externalSystemId = form.externalSystemId.trim();
    }
    if (changes.length > 0) {
      element.zmianyUmowy = changes.map((c) => ({
        rodzajZmiany: c.rodzajZmiany,
        dataZmiany: toCruDate(c.dataZmiany),
        komentarz: c.komentarz,
      }));
    }

    return [element];
  };

  const handleSubmit = async (e, mode = 'immediate') => {
    if (e) e.preventDefault();
    if (!regon) {
      toast.error('Brak REGON jednostki w konfiguracji CRU. Uzupełnij go w ustawieniach.');
      return;
    }

    const dateError = validateCruDate(form.dataZawarciaUmowy, 'Data zawarcia umowy');
    if (dateError) {
      toast.error(dateError);
      return;
    }

    if (!form.umowaNaCzasNieoznaczony && form.okresMetoda === 'daty') {
      if (form.okresOd) {
        const err = validateCruOkresOd(form.okresOd);
        if (err) { toast.error(err); return; }
      }
      if (form.okresDo) {
        const err = validateCruOkresDo(form.okresDo, form.okresOd);
        if (err) { toast.error(err); return; }
      }
    }

    let wartoscFormatted = null;
    if (form.wartoscPrzedmiotu.trim()) {
      const { value, error } = formatCruMoney(form.wartoscPrzedmiotu);
      if (error) {
        toast.error(error);
        return;
      }
      wartoscFormatted = value;
      if (value !== form.wartoscPrzedmiotu.trim()) {
        setField('wartoscPrzedmiotu', value);
      }
    }

    const niejawnoscPrzedmiotuError = validateNiejawnosc(form.niejawnoscPrzedmiotu, 'Jawność przedmiotu umowy');
    if (niejawnoscPrzedmiotuError) {
      toast.error(niejawnoscPrzedmiotuError);
      return;
    }
    const niejawnoscWartosciError = validateNiejawnosc(form.niejawnoscWartosci, 'Jawność wartości umowy');
    if (niejawnoscWartosciError) {
      toast.error(niejawnoscWartosciError);
      return;
    }

    const partyError = validateParties(parties, { forApiPublish: mode === 'immediate' });
    if (partyError) {
      toast.error(partyError);
      return;
    }

    if (form.agreementId.trim() && mode === 'submit' && !isUpdateMode) {
      toast.error('Tryb „Przekaż do publikacji” dotyczy nowych umów. Do aktualizacji użyj przycisku „Aktualizuj” na liście.');
      return;
    }

    if (isUpdateMode) {
      const changeError = validateChangesList(changes, { required: true });
      if (changeError) {
        toast.error(changeError);
        return;
      }
    } else if (form.statusUmowy === 'Nieaktywna') {
      const changeError = validateChangesList(changes, { required: true });
      if (changeError) {
        toast.error('Umowa nieaktywna wymaga dokładnie jednej zmiany w sekcji „Zmiany umowy”.');
        return;
      }
      if (changes.length !== 1) {
        toast.error('Umowa nieaktywna wymaga dokładnie jednej zmiany w sekcji „Zmiany umowy”.');
        return;
      }
    } else if (form.agreementId.trim() && changes.length === 0) {
      toast.error('Aktualizacja wymaga co najmniej jednej zmiany w sekcji „Zmiany umowy”.');
      return;
    }

    const optionalChangeError = validateChangesList(changes);
    if (optionalChangeError) {
      toast.error(optionalChangeError);
      return;
    }

    if (form.statusUmowy === 'Nieaktywna' && !isUpdateMode && changes.length !== 1) {
      toast.error('Umowa nieaktywna wymaga dokładnie jednej zmiany w sekcji „Zmiany umowy”.');
      return;
    }

    setSubmitting(true);
    setSubmitMode(mode);
    setResult(null);
    try {
      const payload = buildPayload(wartoscFormatted);
      let res;
      if (isUpdateMode) {
        res = await api.post(`/cru/agreements/${encodeURIComponent(updateAgreementId)}/update`, payload);
        setResult(res.data);
        const errorCount = res.data?.errorCount ?? 0;
        if (errorCount === 0) {
          toast.success('Umowa została zaktualizowana w CRU');
          onPublished();
        } else {
          toast.error(extractFirstCruError(res.data) || `CRU zgłosiło błędy walidacji (${errorCount}). Sprawdź szczegóły poniżej.`);
        }
        return;
      }
      if (draftId && mode === 'submit') {
        res = await api.put(`/cru/drafts/${draftId}`, payload);
        setResult(res.data);
        toast.success('Szkic został zaktualizowany.');
        onPublished();
        return;
      }
      if (draftId && mode === 'immediate') {
        await api.put(`/cru/drafts/${draftId}`, payload);
        res = await api.post(`/cru/drafts/${draftId}/publish`);
      } else {
        const endpoint = mode === 'submit' ? '/cru/drafts' : '/cru/agreements';
        res = await api.post(endpoint, payload);
      }
      setResult(res.data);
      const errorCount = res.data?.errorCount ?? 0;
      if (mode === 'submit') {
        if (res.data?.draftId) {
          toast.success('Umowa zapisana — oczekuje na publikację. Możesz ją opublikować z listy.');
          onPublished();
        } else {
          toast.error('Nie udało się zapisać szkicu umowy.');
        }
      } else if (errorCount === 0) {
        if (res.data?.alreadyExisted) {
          toast.success('Ta umowa była już opublikowana w CRU.');
        } else {
          toast.success('Umowa została opublikowana w CRU');
        }
        onPublished();
      } else {
        toast.error(extractFirstCruError(res.data) || `CRU zgłosiło błędy walidacji (${errorCount}). Sprawdź szczegóły poniżej.`);
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || (mode === 'submit' ? 'Błąd zapisu szkicu umowy' : 'Błąd podczas publikacji umowy w CRU');
      toast.error(msg);
      setResult(error.response?.data || { error: msg });
    } finally {
      setSubmitting(false);
      setSubmitMode(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {isUpdateMode
                ? `Aktualizacja umowy${updateMeta?.numerUmowy ? ` — ${updateMeta.numerUmowy}` : ''}`
                : draftId
                  ? `Edycja szkicu #${draftId}`
                  : 'Publikacja / aktualizacja umowy CRU'}
            </h3>
            {isUpdateMode && (
              <p className="text-xs text-indigo-800 mt-0.5">
                Umowa opublikowana w CRU
                {updateMeta?.numerSystemowy ? ` • nr systemowy ${updateMeta.numerSystemowy}` : ''}
                — uzupełnij podstawę aktualizacji i wyślij do CRU.
              </p>
            )}
            {draftId && !isUpdateMode && (
              <p className="text-xs text-amber-700 mt-0.5">Szkic oczekuje na publikację — możesz zmienić dane i zapisać lub opublikować od razu.</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loadingDraft ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
        <form onSubmit={(e) => { e.preventDefault(); }} className="p-5 overflow-y-auto space-y-5 bg-gray-50/50">
          {!isUpdateMode && (
          <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
            <div>
              <p className="mb-1">
                Pierwsza strona umowy (jednostka JSFP) jest dodawana automatycznie na podstawie REGON
                z konfiguracji (<strong>{regon || 'brak — uzupełnij w ustawieniach'}</strong>). Umowa musi mieć co najmniej dwie strony.
              </p>
              <p>
                <strong>Przekaż do publikacji</strong> — zapisuje szkic w CRM; publikacja w CRU nastąpi po zatwierdzeniu z listy.
                <strong className="ml-1">Opublikuj od razu</strong> — wysyła do CRU i umowa trafia od razu do statusu „Opublikowana”.
              </p>
            </div>
          </div>
          )}

          {isUpdateMode && (
            <>
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
                <p>
                  Wymagana jest co najmniej jedna zmiana (podstawa aktualizacji). Przy wygaśnięciu lub rozwiązaniu umowy
                  rozważ zmianę statusu umowy na <strong>Nieaktywna</strong>.
                </p>
              </div>
              <SectionCard icon={ClipboardList} title="Podstawa aktualizacji informacji" description="Przyczyna, data i opis zmiany wysyłane do CRU (jak krok 4 w aplikacji CRU).">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">Co najmniej jedna zmiana jest wymagana.</p>
                  <button type="button" onClick={addChange} className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 flex-shrink-0">
                    <Plus className="h-4 w-4 mr-1" /> Dodaj zmianę
                  </button>
                </div>
                {changes.map((ch, idx) => (
                  <div key={idx} className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 grid grid-cols-1 md:grid-cols-3 gap-3 relative">
                    <button type="button" onClick={() => removeChange(idx)} className="absolute top-2 right-2 text-red-500 hover:text-red-700" disabled={changes.length <= 1}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <Field label="Przyczyna dokonania aktualizacji *">
                      <select value={ch.rodzajZmiany} onChange={(e) => setChange(idx, 'rodzajZmiany', e.target.value)} className="inp">
                        {RODZAJ_ZMIANY.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Data, od której zaistniała przyczyna *">
                      <input type="date" value={ch.dataZmiany} onChange={(e) => setChange(idx, 'dataZmiany', e.target.value)} className="inp" />
                    </Field>
                    <div className="md:col-span-1">
                      <Field label="Zwięzły opis zakresu aktualizacji *">
                        <textarea value={ch.komentarz} onChange={(e) => setChange(idx, 'komentarz', e.target.value)} className="inp" rows={2} maxLength={500} placeholder="np. zakończenie umowy" />
                      </Field>
                      <p className="text-xs text-gray-400 text-right mt-0.5">{ch.komentarz.length}/500</p>
                    </div>
                  </div>
                ))}
              </SectionCard>
            </>
          )}

          {/* Dane podstawowe */}
          <SectionCard icon={FileText} title="Dane podstawowe" description="Numer, status i data zawarcia umowy.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Status umowy *">
                <select value={form.statusUmowy} onChange={(e) => setField('statusUmowy', e.target.value)} className="inp">
                  <option value="Aktywna">Aktywna</option>
                  <option value="Nieaktywna">Nieaktywna</option>
                </select>
              </Field>
              <Field label="Data zawarcia umowy *">
                <input
                  type="date"
                  value={form.dataZawarciaUmowy}
                  min={CRU_MIN_DATE}
                  max={getTodayIso()}
                  onChange={(e) => setField('dataZawarciaUmowy', e.target.value)}
                  className="inp"
                />
                <p className="text-xs text-gray-400 mt-1">Od 17.11.2025 do dziś — nie może być w przyszłości.</p>
              </Field>
              <Field label="Numer umowy">
                <input type="text" value={form.numerUmowy} disabled={form.brakNumeruUmowy} onChange={(e) => setField('numerUmowy', e.target.value)} className="inp disabled:bg-gray-100" placeholder={form.brakNumeruUmowy ? 'Umowa bez numeru' : 'np. 02/16.10/92'} />
              </Field>
              <KomorkaWewnetrznaField
                value={form.komorkaWewnetrznaJsfp}
                onChange={(v) => setField('komorkaWewnetrznaJsfp', v)}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <Toggle checked={form.brakNumeruUmowy} onChange={(v) => setField('brakNumeruUmowy', v)} label="Brak numeru umowy" />
              <Toggle checked={form.finansowanaZeSrodkow} onChange={(v) => setField('finansowanaZeSrodkow', v)} label="Finansowana ze środków" />
            </div>

            <details className="rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2">
              <summary className="text-xs font-medium text-gray-600 cursor-pointer select-none">Opcje zaawansowane (identyfikatory)</summary>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                {!isUpdateMode && (
                <Field label="Agreement ID (aktualizacja umowy)">
                  <input type="text" value={form.agreementId} onChange={(e) => setField('agreementId', e.target.value)} className="inp" placeholder="UUID z CRU – puste = nowa umowa" />
                </Field>
                )}
                {isUpdateMode && (
                <Field label="Agreement ID">
                  <input type="text" value={form.agreementId} disabled className="inp disabled:bg-gray-100 font-mono text-xs" />
                </Field>
                )}
                <Field label="ID w systemie CRM (externalSystemId)">
                  <input type="text" value={form.externalSystemId} onChange={(e) => setField('externalSystemId', e.target.value)} className="inp" placeholder="opcjonalnie" />
                </Field>
              </div>
            </details>
          </SectionCard>

          {/* Okres obowiązywania */}
          <SectionCard icon={Calendar} title="Okres obowiązywania" description="Jak długo umowa obowiązuje.">
            <Toggle
              checked={form.umowaNaCzasNieoznaczony}
              onChange={(v) => setField('umowaNaCzasNieoznaczony', v)}
              label="Umowa na czas nieoznaczony"
            />
            {!form.umowaNaCzasNieoznaczony && (
              <>
                <div className="flex gap-4 text-sm">
                  <label className="flex items-center">
                    <input type="radio" name="okresMetoda" checked={form.okresMetoda === 'liczba'} onChange={() => setField('okresMetoda', 'liczba')} className="mr-1" />
                    Liczba + jednostka
                  </label>
                  <label className="flex items-center">
                    <input type="radio" name="okresMetoda" checked={form.okresMetoda === 'daty'} onChange={() => setField('okresMetoda', 'daty')} className="mr-1" />
                    Zakres dat
                  </label>
                </div>
                {form.okresMetoda === 'liczba' ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Liczba">
                      <input type="number" min="1" value={form.okresLiczba} onChange={(e) => setField('okresLiczba', e.target.value)} className="inp" />
                    </Field>
                    <Field label="Jednostka">
                      <select value={form.okresJednostka} onChange={(e) => setField('okresJednostka', e.target.value)} className="inp">
                        {OKRES_JEDNOSTKA.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </Field>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Okres od">
                      <input type="date" value={form.okresOd} min={CRU_OKRES_MIN_DATE} max={getOkresOdMaxIso()} onChange={(e) => setField('okresOd', e.target.value)} className="inp" />
                      <p className="text-xs text-gray-400 mt-1">Od 01.01.2026, max. 1 rok od dziś.</p>
                    </Field>
                    <Field label="Okres do">
                      <input type="date" value={form.okresDo} min={form.okresOd || CRU_OKRES_MIN_DATE} max={CRU_OKRES_MAX_DATE} onChange={(e) => setField('okresDo', e.target.value)} className="inp" />
                      <p className="text-xs text-gray-400 mt-1">Może być w przyszłości (np. koniec umowy).</p>
                    </Field>
                  </div>
                )}
              </>
            )}
          </SectionCard>

          {/* Szczegóły umowy */}
          <SectionCard icon={ClipboardList} title="Szczegóły umowy" description="Przedmiot, wartość oraz wyłączenie jawności.">
            <Field label="Przedmiot umowy">
              <textarea value={form.przedmiotUmowy} onChange={(e) => setField('przedmiotUmowy', e.target.value)} className="inp" rows={2} placeholder="np. Zakup usług informatycznych" />
            </Field>
            <NiejawnoscField
              label="Wyłącz jawność przedmiotu umowy"
              description="Przedmiot nie będzie widoczny publicznie w CRU."
              value={form.niejawnoscPrzedmiotu}
              onChange={(v) => setField('niejawnoscPrzedmiotu', v)}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Wartość przedmiotu (PLN)">
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.wartoscPrzedmiotu}
                  onChange={(e) => setField('wartoscPrzedmiotu', e.target.value)}
                  onBlur={() => {
                    if (!form.wartoscPrzedmiotu.trim()) return;
                    const { value, error } = formatCruMoney(form.wartoscPrzedmiotu);
                    if (!error && value) setField('wartoscPrzedmiotu', value);
                  }}
                  className="inp"
                  placeholder="22000.00"
                />
                <p className="text-xs text-gray-400 mt-1">Kropka dziesiętna, zawsze 2 cyfry po kropce (np. 22000.00).</p>
              </Field>
              <Field label="Opis wartości przedmiotu">
                <input type="text" value={form.opisWartosciPrzedmiotu} onChange={(e) => setField('opisWartosciPrzedmiotu', e.target.value)} className="inp" placeholder="np. Wartość zgodnie z ofertą wykonawcy" />
              </Field>
            </div>
            <NiejawnoscField
              label="Wyłącz jawność wartości umowy"
              description="Wartość nie będzie widoczna publicznie w CRU."
              value={form.niejawnoscWartosci}
              onChange={(v) => setField('niejawnoscWartosci', v)}
            />
          </SectionCard>

          {/* Strony umowy */}
          <SectionCard icon={Users} title="Strony umowy" description="Kontrahenci poza jednostką JSFP. Wymagana co najmniej jedna.">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Dla kontrahenta z Polski (SU01) podaj <strong>NIP lub REGON</strong> — nazwa pobierze się z rejestru REGON.
              </p>
              <button type="button" onClick={addParty} className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 flex-shrink-0">
                <Plus className="h-4 w-4 mr-1" /> Dodaj stronę
              </button>
            </div>
            {parties.map((party, idx) => (
              <div key={idx} className="rounded-lg border border-gray-200 bg-gray-50/40 p-4 space-y-4 relative">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700">
                    <Building2 className="h-3.5 w-3.5" /> Strona {idx + 2}
                  </span>
                  {parties.length > 1 && (
                    <button type="button" onClick={() => removeParty(idx)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Rodzaj">
                    <select value={party.rodzaj} onChange={(e) => setParty(idx, 'rodzaj', e.target.value)} className="inp">
                      {RODZAJ_STRONY.map((r) => <option key={r.code} value={r.code}>{r.code} – {r.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Kraj">
                    <input type="text" value={party.kraj} onChange={(e) => setParty(idx, 'kraj', e.target.value)} className="inp" maxLength={2} placeholder="PL" />
                  </Field>
                  <div className="flex items-end pb-1">
                    <Toggle checked={party.czyKonsorcjum} onChange={(v) => setParty(idx, 'czyKonsorcjum', v)} label="Konsorcjum" />
                  </div>
                </div>
                {party.niejawnosc?.on ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                    Jawność tej strony jest wyłączona — dane identyfikacyjne (nazwa, NIP, REGON) nie są przekazywane do CRU.
                  </p>
                ) : party.rodzaj === 'SU02' ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Imię"><input type="text" value={party.imie} onChange={(e) => setParty(idx, 'imie', e.target.value)} className="inp" /></Field>
                    <Field label="Nazwisko"><input type="text" value={party.nazwisko} onChange={(e) => setParty(idx, 'nazwisko', e.target.value)} className="inp" /></Field>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {(party.kraj || 'PL').toUpperCase() === 'PL' ? (
                      <>
                        {party.rodzaj === 'SU01' && (
                          <Field label="NIP *">
                            <input
                              type="text"
                              value={party.nip}
                              onChange={(e) => setParty(idx, 'nip', e.target.value)}
                              onBlur={(e) => lookupPartyById(idx, { ...party, nip: e.target.value })}
                              className="inp"
                              placeholder="NIP lub REGON poniżej"
                            />
                          </Field>
                        )}
                        <Field label={party.rodzaj === 'SU01' ? 'REGON (alternatywa)' : 'REGON *'}>
                          <input
                            type="text"
                            value={party.regon}
                            onChange={(e) => setParty(idx, 'regon', e.target.value)}
                            onBlur={(e) => lookupPartyById(idx, { ...party, regon: e.target.value })}
                            className="inp"
                          />
                        </Field>
                      </>
                    ) : (
                      <Field label="Nazwa *"><input type="text" value={party.nazwa} onChange={(e) => setParty(idx, 'nazwa', e.target.value)} className="inp" /></Field>
                    )}
                  </div>
                )}
                {party.rodzaj === 'SU01' && (party.kraj || 'PL').toUpperCase() === 'PL' && !party.niejawnosc?.on && (
                  <div className="space-y-2">
                    {party.lookupLoading && (
                      <p className="text-xs text-gray-500 flex items-center gap-2">
                        <LoadingSpinner size="sm" /> Wyszukiwanie kontrahenta (MF / REGON)…
                      </p>
                    )}
                    {party.lookupName && !party.lookupLoading && (
                      <div className="rounded-md border border-green-200 bg-green-50 p-3">
                        <p className="text-sm font-medium text-green-900">{party.lookupName}</p>
                        <p className="text-xs text-green-800 mt-1">
                          {[party.nip && `NIP ${party.nip}`, party.regon && `REGON ${party.regon}`, party.lookupStatus && `VAT: ${party.lookupStatus}`]
                            .filter(Boolean)
                            .join(' • ')}
                        </p>
                        {party.lookupAddress && (
                          <p className="text-xs text-green-700 mt-1">{party.lookupAddress}</p>
                        )}
                        {party.lookupSource === 'LOCAL_CONTRACTOR' && (
                          <p className="text-xs text-green-700 mt-1">Źródło: baza kontrahentów CRM (brak w rejestrze VAT MF)</p>
                        )}
                        {party.lookupSource === 'GUS_REGON' && (
                          <p className="text-xs text-green-700 mt-1">Źródło: rejestr REGON (GUS)</p>
                        )}
                      </div>
                    )}
                    {party.lookupCandidates?.length > 1 && !party.lookupLoading && (
                      <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3 space-y-2">
                        <p className="text-xs font-medium text-indigo-900">
                          {party.lookupInfo || 'Wybierz właściwą działalność (CRU wymaga jednoznacznego REGON):'}
                        </p>
                        <div className="space-y-2">
                          {party.lookupCandidates.map((candidate) => {
                            const selected = party.selectedCandidateRegon === candidate.regon;
                            return (
                              <label
                                key={candidate.regon}
                                className={`flex gap-3 p-2 rounded-md border cursor-pointer text-sm ${
                                  selected ? 'border-indigo-500 bg-white ring-1 ring-indigo-300' : 'border-indigo-100 bg-white/70 hover:bg-white'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`party-candidate-${idx}`}
                                  className="mt-1"
                                  checked={selected}
                                  onChange={() => selectPartyCandidate(idx, candidate)}
                                />
                                <span>
                                  <span className="font-medium text-gray-900 block">
                                    {candidate.name}
                                    {(candidate.cruApiSupported || normalizePartyDigits(candidate.regon).length === 14) ? (
                                      <span className="ml-2 text-xs font-normal text-indigo-700">(API CRM)</span>
                                    ) : (
                                      <span className="ml-2 text-xs font-normal text-amber-700">(tylko przeglądarka CRU)</span>
                                    )}
                                  </span>
                                  <span className="text-xs text-gray-600 block mt-0.5">
                                    {[candidate.silos && `Rejestr: ${candidate.silos}`, `REGON ${candidate.regon}`, candidate.pkd]
                                      .filter(Boolean)
                                      .join(' • ')}
                                  </span>
                                  {candidate.address && (
                                    <span className="text-xs text-gray-500 block mt-0.5">{candidate.address}</span>
                                  )}
                                  {candidate.cruApiSupported === false && (
                                    <span className="text-xs text-amber-700 block mt-1">
                                      Brak 14-cyfrowego REGON w GUS — tę działalność opublikujesz tylko w przeglądarce CRU.
                                    </span>
                                  )}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {party.lookupInfo && !party.lookupLoading && !party.lookupName && !(party.lookupCandidates?.length > 1) && (
                      <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-md p-2">
                        {party.lookupInfo}
                      </p>
                    )}
                    {party.lookupError && !party.lookupLoading && (
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                        {party.lookupError}
                      </p>
                    )}
                  </div>
                )}
                <NiejawnoscField
                  label="Wyłącz jawność tej strony"
                  description="Dane strony nie będą widoczne publicznie w CRU."
                  value={party.niejawnosc || emptyNiejawnosc()}
                  onChange={(v) => setParty(idx, 'niejawnosc', v)}
                />
              </div>
            ))}
          </SectionCard>

          {/* Zmiany umowy — tryb nowej umowy / szkicu */}
          {!isUpdateMode && (
          <SectionCard icon={ClipboardList} title="Zmiany umowy" description="Wymagane przy aktualizacji oraz dla umów nieaktywnych.">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Umowa nieaktywna wymaga <strong>dokładnie jednej</strong> zmiany. Przy aktualizacji — co najmniej jednej.
              </p>
              <button type="button" onClick={addChange} className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 flex-shrink-0">
                <Plus className="h-4 w-4 mr-1" /> Dodaj zmianę
              </button>
            </div>
            {changes.length === 0 && <p className="text-xs text-gray-400">Brak zmian.</p>}
            {changes.map((ch, idx) => (
              <div key={idx} className="rounded-lg border border-gray-200 bg-gray-50/40 p-4 grid grid-cols-1 md:grid-cols-3 gap-3 relative">
                <button type="button" onClick={() => removeChange(idx)} className="absolute top-2 right-2 text-red-500 hover:text-red-700">
                  <Trash2 className="h-4 w-4" />
                </button>
                <Field label="Rodzaj zmiany">
                  <select value={ch.rodzajZmiany} onChange={(e) => setChange(idx, 'rodzajZmiany', e.target.value)} className="inp">
                    {RODZAJ_ZMIANY.map((r) => <option key={r.code} value={r.code}>{r.code} – {r.label}</option>)}
                  </select>
                </Field>
                <Field label="Data zmiany">
                  <input type="date" value={ch.dataZmiany} onChange={(e) => setChange(idx, 'dataZmiany', e.target.value)} className="inp" />
                </Field>
                <Field label="Komentarz">
                  <input type="text" value={ch.komentarz} onChange={(e) => setChange(idx, 'komentarz', e.target.value)} className="inp" maxLength={500} />
                </Field>
              </div>
            ))}
          </SectionCard>
          )}

          {result && <CruResultPanel result={result} />}

          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-3 border-t sticky bottom-0 bg-gray-50/95 backdrop-blur -mx-5 px-5 pb-1">
            {cruBrowserPartyRequired && !isUpdateMode && (
              <div className="sm:mr-auto sm:max-w-md text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2.5 mb-1 sm:mb-0">
                Wybrana działalność (CEIDG) nie ma 14-cyfrowego REGON — API CRU jej nie przyjmie.
                {' '}
                <a href={cruWebUrl} target="_blank" rel="noopener noreferrer" className="font-medium underline hover:text-amber-950">
                  Opublikuj umowę w przeglądarce CRU
                </a>
                {' '}(możesz zapisać szkic tutaj).
              </div>
            )}
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
              Anuluj
            </button>
            {isUpdateMode ? (
              <button
                type="button"
                disabled={submitting}
                onClick={(e) => handleSubmit(e, 'update')}
                className="flex items-center justify-center px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting && submitMode === 'update' ? <><div className="mr-2"><LoadingSpinner size="sm" /></div>Wysyłanie...</> : <><Send className="h-4 w-4 mr-2" /> Wyślij aktualizację do CRU</>}
              </button>
            ) : (
            <>
            <button
              type="button"
              disabled={submitting}
              onClick={(e) => handleSubmit(e, 'submit')}
              className="flex items-center justify-center px-4 py-2 text-sm font-medium text-amber-900 bg-amber-100 border border-amber-300 rounded-md hover:bg-amber-200 disabled:opacity-50"
            >
              {submitting && submitMode === 'submit' ? <LoadingSpinner size="sm" /> : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {draftId ? 'Zapisz szkic' : 'Przekaż do publikacji'}
                </>
              )}
            </button>
            <button
              type="button"
              disabled={submitting || cruBrowserPartyRequired}
              title={cruBrowserPartyRequired ? 'Ta działalność wymaga publikacji w przeglądarce CRU' : undefined}
              onClick={(e) => handleSubmit(e, 'immediate')}
              className="flex items-center justify-center px-5 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting && submitMode === 'immediate' ? <><div className="mr-2"><LoadingSpinner size="sm" /></div>Wysyłanie...</> : <><Upload className="h-4 w-4 mr-2" /> Opublikuj od razu</>}
            </button>
            </>
            )}
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

export default UmowyCru;
